import React, { useState, useEffect, useCallback, useRef } from "react";
import { TIP_PRESET_PERCENTAGES, DEFAULT_TIP_PERCENTAGE } from "../constants/tips";
import AddressAutocompleteInput from "../components/AddressAutocompleteInput";
import { useNavigate, useSearchParams } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import AuthModal from "../components/AuthModal";
import ProductQuickViewModal from "../components/ProductQuickViewModal";
import {
  BlockerChipsPanel,
  NotesPanel,
  CustomerNoteField,
  ServicePricingSelector,
} from "../components/BookingFlowPanels";
import SectionStatusHeader from "../components/SectionStatusHeader";
import SummaryReturnPrompt from "../components/SummaryReturnPrompt";
import CustomerHeader from "../components/CustomerHeader";
import CustomerFooter from "../components/CustomerFooter";
import CancellationPolicyModal from "../components/CancellationPolicyModal";
import { NON_REFUNDABLE_PROCESSING_FEE_SHORT_NOTICE } from "../utils/transactionPolicyText";
import {
  clearDirectBookingHold,
  getDirectBookingHold,
  saveDirectBookingHold,
} from "../utils/bookingReservationHold";
import { buildSlotUnavailableNotice } from "../utils/slotAvailabilityNotice";
import { useCart } from "../hooks/useCart";
import {
  BOOKING_BILLING_ADDRESS_RETURN_TARGET,
  buildSummaryBlockers,
  buildSummarySoftWarnings,
  getCollapsibleSectionStyle,
  getBillingAddressReturnTarget,
  getSectionBlockStyle,
  isBillingAddressRequired,
  shouldShowBillingAddressReturnPrompt,
} from "../utils/bookingFlowRules";
// F1/F2: service pricing engine (display mirror - server remains authoritative)
import {
  isSelectionDrivenService,
  resolveServicePricing,
  resolveServiceLines,
} from "../utils/servicePricing";

const FreelancerBooking = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL parameters
  const staffId = searchParams.get("staffId");
  const serviceId = searchParams.get("serviceId");
  const lookId = searchParams.get("lookId");

  // Core booking data
  const [shop, setShop] = useState(null);
  const [staff, setStaff] = useState(null);
  const [freelancerBusiness, setFreelancerBusiness] = useState(null);
  const [service, setService] = useState(null);
  const [serviceBundle, setServiceBundle] = useState([]);
  const [selectedBundleDurationMinutes, setSelectedBundleDurationMinutes] =
    useState(0);
  // F1/F2: customer selection for engine-priced services
  const [serviceQuantity, setServiceQuantity] = useState(1);
  const [serviceHours, setServiceHours] = useState(1);
  const [serviceTierId, setServiceTierId] = useState(null);
  // P1/P2 (F3): each add-on line keeps its OWN selection, keyed by service id,
  // so changing one line can never mutate another.
  const [addOnSelections, setAddOnSelections] = useState({});
  // Optional note the customer can leave for the professional
  const [customerNote, setCustomerNote] = useState("");
  const [shopServices, setShopServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { itemCount } = useCart();
  const BOOKING_SLOT_HOLD_NOTICE =
    "we confirm this time again when you continue to payment. If it has been taken, you'll be asked to pick another time - you will not be charged.";

  // Customer information
  const [customerInfo, setCustomerInfo] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [customerAddress, setCustomerAddress] = useState({
    street: "",
    city: "",
    state: "",
    zipCode: "",
    country: "US",
  });

  // Service location and travel charges (Phase C11.B)
  const [serviceLocation, setServiceLocation] = useState("freelancer"); // 'freelancer' or 'client'
  const [clientAddress, setClientAddress] = useState({
    street: "",
    city: "",
    state: "",
    zip: "",
  });
  const [travelFeeAccepted, setTravelFeeAccepted] = useState(false);

  // Calendar and time selection
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);

  // Products
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [showQuickViewModal, setShowQuickViewModal] = useState(false);
  const billingAddressSectionRef = useRef(null);
  const billingStreetInputRef = useRef(null);
  const summarySectionRef = useRef(null);
  const customerInfoSectionRef = useRef(null);
  const serviceLocationSectionRef = useRef(null);
  const dateTimeSectionRef = useRef(null);
  // F2: the service card holds the option picker, so a pending-choice blocker
  // can send the customer back to it.
  const serviceCardRef = useRef(null);
  const productsSectionRef = useRef(null);
  const policySectionRef = useRef(null);
  const [expandedSection, setExpandedSection] = useState("customerInfo");
  // F2: set when a service change invalidated the chosen time slot.
  const [slotInvalidatedNotice, setSlotInvalidatedNotice] = useState("");
  const [billingAddressReturnTarget, setBillingAddressReturnTarget] = useState(
    BOOKING_BILLING_ADDRESS_RETURN_TARGET.PRODUCTS,
  );
  const [summaryReturnSection, setSummaryReturnSection] = useState("");
  const [showServiceSelector, setShowServiceSelector] = useState(false);
  const getWaitlistDateLabels = (waitlistEntry) => {
    const dates = Array.isArray(waitlistEntry?.preferredDates)
      ? waitlistEntry.preferredDates
      : [];

    return dates
      .map((dateValue) => {
        const normalizedDate = String(dateValue || "").slice(0, 10);
        return normalizedDate ? formatDisplayDate(normalizedDate) : "";
      })
      .filter(Boolean);
  };

  const getWaitlistServiceLabels = (waitlistEntry) => {
    const bundle = Array.isArray(waitlistEntry?.serviceBundle)
      ? waitlistEntry.serviceBundle
      : [];
    const names = bundle
      .map((svc) => String(svc?.name || "").trim())
      .filter(Boolean);

    return names.length > 0 ? names : [service?.name || "Selected service"];
  };

  // Authentication state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode] = useState("login");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasChosenGuest, setHasChosenGuest] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [showCartWarningModal, setShowCartWarningModal] = useState(false);
  const [hasAutoShownCartWarning, setHasAutoShownCartWarning] =
    useState(false);
  const [cartWarningAcknowledged, setCartWarningAcknowledged] =
    useState(false);

  // Cancellation policy
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [cancellationPolicy, setCancellationPolicy] = useState(null);

  const [waitlistCompatibility, setWaitlistCompatibility] = useState(null);
  const [waitlistCompatibilityLoading, setWaitlistCompatibilityLoading] =
    useState(false);
  const [waitlistMismatchAcknowledged, setWaitlistMismatchAcknowledged] =
    useState(false);
  const waitlistMismatchSectionRef = useRef(null);

  // Waitlist join state
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistMessage, setWaitlistMessage] = useState({ type: "", text: "" });
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [pendingWaitlistReplacement, setPendingWaitlistReplacement] = useState(null);
  const [activeWaitlistConflict, setActiveWaitlistConflict] = useState(null);
  const [nextAvailableSlots, setNextAvailableSlots] = useState([]);
  const [nextAvailableError, setNextAvailableError] = useState("");
  const waitlistDurationDays = 7;

  const normalizeServiceDuration = (svc) =>
    Number(svc?.durationMinutes || svc?.duration || 0) || 0;

  const normalizeServicePrice = (svc) => Number(svc?.price || 0) || 0;

  const getProductVariantSummary = (product = {}) => {
    const attributes = Array.isArray(product.variantAttributes)
      ? product.variantAttributes
      : [];

    if (attributes.length > 0) {
      return attributes
        .map((attribute) => {
          const type = String(
            attribute?.dimensionName ||
              attribute?.dimensionKey ||
              attribute?.name ||
              attribute?.type ||
              "Variant",
          ).trim();
          const value = String(attribute?.label || attribute?.value || "").trim();
          return value ? `${type}: ${value}` : type;
        })
        .filter(Boolean)
        .join(" • ");
    }

    const fallbackType = String(
      product.variantType || product.variantLabel || product.variantName || "",
    ).trim();
    const fallbackValue = String(product.variantValue || "").trim();

    if (fallbackType && fallbackValue) {
      return `${fallbackType}: ${fallbackValue}`;
    }

    return fallbackType;
  };

  const getBundleServices = () => {
    const orderedServices = [];
    if (service) {
      orderedServices.push(service);
    }

    for (const bundleService of serviceBundle) {
      if (!bundleService?._id || String(bundleService._id) === String(service?._id)) {
        continue;
      }

      if (
        orderedServices.some(
          (existing) => String(existing?._id) === String(bundleService._id),
        )
      ) {
        continue;
      }

      orderedServices.push(bundleService);
    }

    return orderedServices;
  };

  const getBundleDuration = () =>
    getBundleServices().reduce(
      (sum, svc) => sum + normalizeServiceDuration(svc),
      0,
    );

  const getBundlePrice = () =>
    getBundleServices().reduce((sum, svc) => sum + normalizeServicePrice(svc), 0);

  const bundleDurationMinutes = getBundleDuration();
  const bundlePrice = getBundlePrice();
  const bundleServiceCount = getBundleServices().length;
  const bundleServices = getBundleServices();
  const primaryBundleService = bundleServices[0] || service || null;

  // P1/P2 (F3): resolve EVERY bundle line through the engine, not just a lone
  // primary. The primary keeps the top-level quantity/hours/tier state; each
  // add-on owns its selection in `addOnSelections`, so one line can never
  // mutate another and the primary stays editable with add-ons present.
  const primaryServiceKey = String(primaryBundleService?._id || "");
  const serviceSelection = {
    quantity: serviceQuantity,
    hours: serviceHours,
    tierId: serviceTierId,
  };
  const resolvedBundleLines = resolveServiceLines(
    bundleServices.map((svc) => ({
      service: svc,
      selection:
        String(svc._id) === primaryServiceKey
          ? serviceSelection
          : addOnSelections[String(svc._id)] || {},
    })),
  );
  // The primary is always the first line (order comes from getBundleServices).
  const resolvedPrimaryLine = resolvedBundleLines.lines[0] || null;
  const anyLineSelectionDriven = bundleServices.some(isSelectionDrivenService);

  // A line that still needs a choice has NO price and NO length yet. For a range
  // service the stored `durationMinutes` is the LONGEST option, so deriving a
  // length from that default would offer slots the customer cannot get.
  const pendingSelectionLines = resolvedBundleLines.lines.filter(
    (line) => line && line.requiresSelection,
  );
  const primaryNeedsChoice = Boolean(
    resolvedPrimaryLine && resolvedPrimaryLine.requiresSelection,
  );
  const pendingAddOnNames = bundleServices
    .slice(1)
    .filter((svc) =>
      pendingSelectionLines.some(
        (line) => String(line.serviceId) === String(svc?._id),
      ),
    )
    .map((svc) => svc.name);
  const selectionPendingLabel = primaryNeedsChoice
    ? resolvedPrimaryLine?.label || primaryBundleService?.name || "your service"
    : pendingAddOnNames[0] || "your service";

  const baseBundleDurationMinutes = Math.max(
    1,
    selectedBundleDurationMinutes ||
      bundleDurationMinutes ||
      normalizeServiceDuration(service) ||
      0,
  );
  // The engine owns the duration + price as soon as ANY line is choice-driven.
  const effectiveBundleDurationMinutes =
    anyLineSelectionDriven && resolvedBundleLines.totalMinutes > 0
      ? Math.max(1, resolvedBundleLines.totalMinutes)
      : baseBundleDurationMinutes;
  // Server-authoritative price; the mirror is for display only.
  const effectiveServicePrice = anyLineSelectionDriven
    ? resolvedBundleLines.totalPrice
    : bundlePrice;

  // F2: a choice-based service stores derived aliases - `price` is the lowest
  // option and `durationMinutes` the longest one. Those two do not belong
  // together, so until the customer picks we show neither rather than a
  // misleading pair.
  const isChoiceBasedService = isSelectionDrivenService(primaryBundleService);
  // A pending choice on ANY line blocks progression - an add-on with no option
  // chosen has no price and no length to reserve.
  const primarySelectionPending = pendingSelectionLines.length > 0;
  // The primary row/card is labelled with the PRIMARY service, so it must show
  // the primary's OWN price and duration - never the bundle totals. Add-ons are
  // itemised separately below, and the footer already prices the primary alone
  // via `primaryRowPrice`. Showing the bundle totals here double-counted the
  // add-ons and made the primary's duration look wrong.
  // `effectiveServicePrice` / `effectiveBundleDurationMinutes` stay the real
  // bundle totals - they still drive totals and the slot search.
  const primaryRowDuration =
    isChoiceBasedService && resolvedPrimaryLine
      ? resolvedPrimaryLine.totalMinutes
      : normalizeServiceDuration(primaryBundleService || service);
  const displayedPrimaryPrice =
    isChoiceBasedService && resolvedPrimaryLine
      ? resolvedPrimaryLine.totalPrice
      : normalizeServicePrice(primaryBundleService || service);
  const displayedPrimaryDuration = primaryRowDuration;
  // e.g. "Nail Art × 5", "Event Styling × 3", "Box Braids (Mid-back)"
  const primaryDisplayName =
    (resolvedPrimaryLine && resolvedPrimaryLine.label) ||
    primaryBundleService?.name ||
    "Service";
  // The summary lists the primary service and each add-on separately, so this
  // row needs the primary's OWN price - never the bundle total.
  const primaryRowPrice =
    isChoiceBasedService && resolvedPrimaryLine
      ? resolvedPrimaryLine.totalPrice
      : normalizeServicePrice(service);

  const handleServiceSelectionChange = (patch = {}) => {
    if (patch.quantity !== undefined) setServiceQuantity(patch.quantity);
    if (patch.hours !== undefined) setServiceHours(patch.hours);
    if (patch.tierId !== undefined) setServiceTierId(patch.tierId);
  };

  // P1/P2 (F3): each add-on keeps its own selection, keyed by service id.
  const handleAddOnSelectionChange = (serviceKey, patch = {}) => {
    const key = String(serviceKey);
    setAddOnSelections((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), ...patch },
    }));
  };

  // F1/F2: reset the customer's pricing selection when the PRIMARY service
  // changes. It must NOT reset when an add-on is added or removed - that is what
  // made the stepper look locked once a bundle existed (S2).
  useEffect(() => {
    setServiceQuantity(1);
    setServiceHours(1);
    setServiceTierId(null);
  }, [serviceId]);

  // P1/F3: an add-on that left the bundle must not keep a stale selection.
  useEffect(() => {
    setAddOnSelections((prev) => {
      const liveIds = new Set((serviceBundle || []).map((svc) => String(svc?._id)));
      const next = {};
      let changed = false;
      Object.keys(prev).forEach((id) => {
        if (liveIds.has(id)) next[id] = prev[id];
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [serviceBundle]);

  useEffect(() => {
    if (expandedSection !== "summary" || !selectedTime || !customerInfo.email?.trim()) {
      setWaitlistCompatibility(null);
      setWaitlistMismatchAcknowledged(false);
      return;
    }

    const controller = new AbortController();
    const checkWaitlistCompatibility = async () => {
      setWaitlistCompatibilityLoading(true);
      try {
        const selectedBundle = [];
        if (service) {
          selectedBundle.push(service);
        }
        for (const bundleService of serviceBundle) {
          if (!bundleService?._id || String(bundleService._id) === String(service?._id)) {
            continue;
          }
          if (
            selectedBundle.some(
              (existing) => String(existing?._id) === String(bundleService._id),
            )
          ) {
            continue;
          }
          selectedBundle.push(bundleService);
        }
        const compatibilityBundle = selectedBundle.map((svc) => ({
          serviceId: svc._id,
          name: svc.name,
          durationMinutes: normalizeServiceDuration(svc),
          price: normalizeServicePrice(svc),
        }));
        const token = localStorage.getItem("ogestyle_token");
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/waitlist/checkout-compatibility`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            signal: controller.signal,
            body: JSON.stringify({
              shopId: freelancerBusiness?._id || shop?._id,
              staffId,
              serviceId,
              serviceBundle: compatibilityBundle,
              serviceBundleDuration: effectiveBundleDurationMinutes,
              customerEmail: customerInfo.email,
            }),
          },
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || "Waitlist compatibility check failed");
        }
        setWaitlistCompatibility(data);
        setWaitlistMismatchAcknowledged(false);
      } catch (err) {
        if (err.name !== "AbortError") {
          setWaitlistCompatibility(null);
          setWaitlistMismatchAcknowledged(false);
        }
      } finally {
        if (!controller.signal.aborted) {
          setWaitlistCompatibilityLoading(false);
        }
      }
    };

    checkWaitlistCompatibility();
    return () => controller.abort();
  }, [
    expandedSection,
    selectedTime,
    selectedDate,
    shop?._id,
    freelancerBusiness?._id,
    staffId,
    serviceId,
    serviceBundle,
    service,
    customerInfo.email,
    effectiveBundleDurationMinutes,
  ]);

  const addonBundleServices = bundleServices.slice(1);
  const selectableBundleServices = [
    ...(service ? [service] : []),
    ...shopServices.filter((svc) => String(svc?._id) !== String(service?._id)),
  ];
  const hasAdditionalBundleServices =
    selectableBundleServices.filter(
      (svc) => String(svc?._id) !== String(service?._id),
    ).length > 0;

  const toggleBundleService = (svc) => {
    if (!svc?._id || String(svc._id) === String(service?._id)) {
      return;
    }

    setServiceBundle((prev) => {
      const alreadySelected = prev.some(
        (existing) => String(existing._id) === String(svc._id),
      );

      if (alreadySelected) {
        return prev.filter((existing) => String(existing._id) !== String(svc._id));
      }

      return [...prev, svc];
    });
  };

  const handleManageAdditionalServices = () => {
    if (selectedTime) {
      setSelectedTime("");
      setShowServiceSelector(true);
      scrollToSection(dateTimeSectionRef);
      return;
    }

    setShowServiceSelector((prev) => !prev);
  };

  useEffect(() => {
    const lockedDuration = Math.max(
      1,
      bundleDurationMinutes || normalizeServiceDuration(service) || 0,
    );
    setSelectedBundleDurationMinutes(lockedDuration);
  }, [bundleDurationMinutes, service]);

  // SMS consent state (TCPA compliance)
  const [smsConsent, setSmsConsent] = useState(false);

  // Preview mode state (for testing before go-live)
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  // Tip selection state (12%, 15%, 18%, 20%)
  const [tipPercentage, setTipPercentage] = useState(DEFAULT_TIP_PERCENTAGE);

  // Same as CustomerBooking: presets decide for the customer, so allow a flat amount they
  // choose - or nothing. The backend accepts a flat `tipAmount` and only derives from a
  // percentage when one is > 0.
  const [tipIsCustom, setTipIsCustom] = useState(false);
  const [customTipAmount, setCustomTipAmount] = useState("");

  // How much of the service value a tip may reach. 1 = 100%, i.e. a tip cannot exceed the
  // service itself. A cap is dispute prevention, not stinginess - a mistyped "500" on a
  // $40 service is a chargeback, and the money is already in the professional's payout.
  const MAX_TIP_MULTIPLE = 1;
  // A free service has nothing to take 100% of, and a $0 cap would make the field dead.
  const TIP_CEILING_FALLBACK = 100;
  const maxCustomTip = (serviceTotalForTip) => {
    const base = Number(serviceTotalForTip) || 0;
    return base > 0 ? base * MAX_TIP_MULTIPLE : TIP_CEILING_FALLBACK;
  };
  const parseCustomTip = () => {
    const raw = String(customTipAmount).trim();
    if (raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };
  // Live validation, so a bad amount is visible as it is typed rather than only when Pay
  // is pressed. The submit guard below reuses this, so the two can never disagree.
  const customTipErrorFor = (serviceTotalForTip) => {
    if (!tipIsCustom) return "";
    if (String(customTipAmount).trim() === "") return "";
    const value = parseCustomTip();
    if (value === null) return "Enter a valid amount.";
    if (value < 0) return "A tip cannot be negative.";
    const ceiling = maxCustomTip(serviceTotalForTip);
    if (value > ceiling) {
      return `Tip cannot be more than $${ceiling.toFixed(2)} (100% of the service).`;
    }
    return "";
  };

  // Responsive design
  const [isMobile, setIsMobile] = useState(false);

  // Tax preview state
  const [taxPreview, setTaxPreview] = useState({
    taxAmount: 0,
    taxRate: 0,
    calculatedAtCheckout: true,
  });
  const [taxLoading, setTaxLoading] = useState(false);
  const taxPreviewRequestIdRef = useRef(0);

  const selectedProductsTaxSignature = JSON.stringify(
    selectedProducts.map((product) => ({
      productId: product.productId,
      quantity: product.quantity,
      price: product.price,
      stripeTaxCode: product.stripeTaxCode || "",
    })),
  );

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Check for preview mode from sessionStorage
  useEffect(() => {
    const previewToken = sessionStorage.getItem("ogestyle_preview_token");
    if (previewToken) {
      setIsPreviewMode(true);
    }
  }, []);

  useEffect(() => {
    if (itemCount > 0 && !cartWarningAcknowledged && !hasAutoShownCartWarning) {
      setShowCartWarningModal(true);
      setHasAutoShownCartWarning(true);
    }
  }, [itemCount, cartWarningAcknowledged, hasAutoShownCartWarning]);

  // Fetch real-time tax estimate when products + billing address are ready
  useEffect(() => {
    const fetchTaxPreview = async () => {
      const currentRequestId = ++taxPreviewRequestIdRef.current;

      if (selectedProducts.length === 0) {
        setTaxPreview({ taxAmount: 0, taxRate: 0, calculatedAtCheckout: true });
        setTaxLoading(false);
        return;
      }

      const hasAddress =
        customerAddress.street &&
        customerAddress.city &&
        customerAddress.state &&
        customerAddress.zipCode;

      if (!hasAddress) {
        setTaxPreview({ taxAmount: 0, taxRate: 0, calculatedAtCheckout: true });
        setTaxLoading(false);
        return;
      }

      setTaxLoading(true);

      try {
        const items = selectedProducts.map((p) => ({
          productId: p.productId,
          quantity: p.quantity,
          price: p.price,
          stripeTaxCode: p.stripeTaxCode,
        }));
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/tax/estimate`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              items,
              billingAddress: {
                street: customerAddress.street,
                city: customerAddress.city,
                state: customerAddress.state,
                zipCode: customerAddress.zipCode,
                country: customerAddress.country || "US",
              },
            }),
          },
        );

        if (currentRequestId !== taxPreviewRequestIdRef.current) {
          return;
        }

        if (response.ok) {
          const data = await response.json();
          if (currentRequestId !== taxPreviewRequestIdRef.current) {
            return;
          }

          setTaxPreview({
            taxAmount: data.taxAmount,
            taxRate: data.taxRate,
            calculatedAtCheckout: false,
          });
        } else {
          setTaxPreview({ taxAmount: 0, taxRate: 0, calculatedAtCheckout: true });
        }
      } catch {
        if (currentRequestId !== taxPreviewRequestIdRef.current) {
          return;
        }

        setTaxPreview({ taxAmount: 0, taxRate: 0, calculatedAtCheckout: true });
      } finally {
        if (currentRequestId === taxPreviewRequestIdRef.current) {
          setTaxLoading(false);
        }
      }
    };

    fetchTaxPreview();
  }, [
    selectedProducts,
    selectedProductsTaxSignature,
    customerAddress.street,
    customerAddress.city,
    customerAddress.state,
    customerAddress.zipCode,
    customerAddress.country,
  ]);

  // Authentication check
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("ogestyle_token");
      if (token) {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/users/profile`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (response.ok) {
            const user = await response.json();
            setIsAuthenticated(true);
            setCustomerInfo({
              firstName: user.firstName || user.name?.split(" ")[0] || "",
              lastName: user.lastName || user.name?.split(" ")[1] || "",
              email: user.email || "",
              phone: user.phone || "",
            });

            // Populate client address from billing address for "At My Location" option
            if (user.billingAddress) {
              setClientAddress({
                street: user.billingAddress.street || "",
                city: user.billingAddress.city || "",
                state: user.billingAddress.state || "",
                zip:
                  user.billingAddress.zip || user.billingAddress.zipCode || "",
              });

              setCustomerAddress({
                street: user.billingAddress.street || "",
                city: user.billingAddress.city || "",
                state: user.billingAddress.state || "",
                zipCode:
                  user.billingAddress.zipCode || user.billingAddress.zip || "",
                country: user.billingAddress.country || "US",
              });
            }
          } else {
            localStorage.removeItem("ogestyle_token");
            setIsAuthenticated(false);
          }
        } catch (error) {
          setIsAuthenticated(false);
        }
      } else {
        // No token - require authentication immediately (matches business-professional flow)
        setIsAuthenticated(false);
        setShowAuthModal(true);
      }
    };

    checkAuth();
  }, []);

  // Fetch cancellation policy on mount
  useEffect(() => {
    const baseURL =
      process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";
    fetch(`${baseURL}/api/platform-settings/cancellation-policy`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setCancellationPolicy(data);
      })
      .catch(() => {
        // Non-critical fallback to local defaults
      });
  }, []);

  // Load staff and service data
  useEffect(() => {
    if (!staffId || !serviceId) {
      setError("Missing booking information");
      setLoading(false);
      return;
    }

    const loadBookingData = async () => {
      try {
        const [staffRes, serviceRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/staff/slug/${staffId}`),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/api/services/${serviceId}`),
        ]);

        if (!staffRes.ok || !serviceRes.ok) {
          throw new Error("Failed to load booking information");
        }

        const [staffData, serviceData] = await Promise.all([
          staffRes.json(),
          serviceRes.json(),
        ]);
        setStaff(staffData);
        setService(serviceData);
        setShopServices(staffData.services || []);

        // Mirror CustomerBooking: freelancers use their virtual shop/location for availability.
        if (staffData.shopIds && staffData.shopIds.length > 0) {
          const rawVirtualShop = staffData.shopIds[0];
          const virtualShopId =
            rawVirtualShop?._id || rawVirtualShop?.id || rawVirtualShop || null;
          const virtualShopIdStr =
            typeof virtualShopId === "string"
              ? virtualShopId
              : virtualShopId?.toString?.() || "";

          if (virtualShopIdStr) {
            try {
              const locationRes = await fetch(
                `${process.env.REACT_APP_API_BASE_URL}/api/shops/${encodeURIComponent(virtualShopIdStr)}`,
              );

              if (locationRes.ok) {
                const locationData = await locationRes.json();
                setFreelancerBusiness(locationData);
                setShop(locationData);
              }
            } catch (_err) {
            }
          }
        }

        setLoading(false);
      } catch (err) {
        setError("Failed to load booking information. Please try again.");
        setLoading(false);
      }
    };

    loadBookingData();
  }, [staffId, serviceId]);

  // Load recommended products when staff and service data are available
  useEffect(() => {
    if (staff && service && !loading) {
      fetchRecommendedProducts();
    }
    // Intentionally tied to resolved booking entities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staff, service, loading]);

  // Load available time slots when date changes.
  // F2/P2: while a choice-based service still has no option selected the length is
  // UNKNOWN (for a range service the stored durationMinutes is the LONGEST option),
  // so hold the grid instead of deriving a length from that default.
  useEffect(() => {
    if (selectedDate && staff && service && !primarySelectionPending) {
      setAvailableSlots([]);
      fetchAvailableSlots();
      setWaitlistJoined(false);
      setWaitlistMessage({ type: "", text: "" });
      setPendingWaitlistReplacement(null);
      setActiveWaitlistConflict(null);
      setNextAvailableSlots([]);
      setNextAvailableError("");
    } else if (primarySelectionPending) {
      // No resolved length yet: clear the grid rather than offer times computed
      // from the longest option.
      setAvailableSlots([]);
      setSelectedTime(null);
    }
    // Intentionally keyed to selected date + booking identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedDate,
    staff,
    service,
    effectiveBundleDurationMinutes,
    primarySelectionPending,
  ]);

  useEffect(() => {
    if (
      selectedDate &&
      !calendarLoading &&
      availableSlots.length === 0 &&
      !selectedTime &&
      staff &&
      service
    ) {
      fetchNextAvailableSlots(selectedDate, 5);
    }
    // Intentionally follows selected-day availability checks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, calendarLoading, availableSlots, selectedTime, staff, service, effectiveBundleDurationMinutes]);

  // Reset selected time whenever bundle duration changes.
  // F2: any change that alters the appointment length invalidates the chosen
  // slot, so the customer must pick a time again. Name/email/phone, the
  // address, the note and the product selection are untouched - only the
  // schedule is affected.
  useEffect(() => {
    if (!staff || !service) {
      return;
    }
    const hadSelection = Boolean(selectedTime);
    setSelectedTime("");
    if (hadSelection) {
      setSlotInvalidatedNotice(
        "Your time slot was cleared because the service changed. Please pick a new time.",
      );
      // Send them back to the slot picker only if they had moved past it.
      setExpandedSection((prev) =>
        ["products", "summary"].includes(prev) ? "dateTime" : prev,
      );
    }
    // Intentionally keyed to bundle duration so selected slot cannot go stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveBundleDurationMinutes]);

  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const formatDisplayDate = (dateKey) => {
    const parsed = new Date(`${dateKey}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) return dateKey;
    return parsed.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime12 = (time24) => {
    const [h, m] = String(time24 || "").split(":").map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return String(time24 || "");
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 || 12;
    return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
  };

  const nextAvailableGroupedSlots = Object.values(
    nextAvailableSlots.reduce((acc, slot) => {
      const dateKey = slot.date;
      if (!dateKey) return acc;
      if (!acc[dateKey]) {
        acc[dateKey] = {
          dateKey,
          displayDate: slot.displayDate || formatDisplayDate(dateKey),
          slots: [],
        };
      }
      acc[dateKey].slots.push(slot);
      return acc;
    }, {}),
  );

  const resolveBookingLocationId = useCallback(() => {
    if (!staff) return null;

    return (
      shop?._id ||
      shop?._id?.toString?.() ||
      staff?.activeLocationId?._id ||
      staff?.activeLocationId ||
      staff?.shopId?._id ||
      staff?.shopId ||
      staff?.shopIds?.[0]?._id ||
      staff?.shopIds?.[0] ||
      null
    );
  }, [shop, staff]);

  useEffect(() => {
    if (isAuthenticated && expandedSection === "customerInfo") {
      setExpandedSection("serviceLocation");
    }
  }, [isAuthenticated, expandedSection]);

  const isDateUnavailable = useCallback((date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  }, []);

  const fetchAvailableSlots = async () => {
    if (!selectedDate || !staff || !service) return;

    setCalendarLoading(true);
    try {
      // Format date without timezone conversion to avoid day-shift bug
      const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
      const duration = effectiveBundleDurationMinutes;
      const effectiveStaffId = String(staff?._id || staffId || "").trim();
      const primaryServiceId = String(service?._id || serviceId || "").trim();
      const serviceIdsCsv = bundleServices
        .map((svc) => String(svc?._id || "").trim())
        .filter(Boolean)
        .join(",");

      // Use one day-slots endpoint and pass effective bundle duration under multiple keys
      // for backend compatibility across environments.
      const dayResponse = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/available-slots?staffId=${effectiveStaffId}&date=${dateStr}&serviceId=${primaryServiceId}&serviceDuration=${duration}&duration=${duration}&durationMinutes=${duration}&serviceBundleDuration=${duration}&bundleDurationMinutes=${duration}&serviceIds=${encodeURIComponent(serviceIdsCsv)}&fresh=1`,
      );

      if (!dayResponse.ok) {
        throw new Error("Failed to load available slots");
      }

      const dayData = await dayResponse.json();
      const daySlots = dayData.availableSlots || dayData.slots || [];
      setAvailableSlots(
        daySlots.map((slot, index) => ({
          ...slot,
          time24: slot.time24 || slot.start || String(index),
          endTime: slot.endTime || slot.end || "",
          time12: slot.time12 || slot.display || slot.time24 || slot.start || "",
        })),
      );
    } catch (err) {
      setAvailableSlots([]);
    }
    setCalendarLoading(false);
  };

  const fetchNextAvailableSlots = async (currentDate, days = 5) => {
    try {
      const queryServiceDuration =
        Number(effectiveBundleDurationMinutes || normalizeServiceDuration(service) || 0) || 0;
      if (!queryServiceDuration) {
        throw new Error(
          "Selected service is missing a valid duration. Please refresh and try again.",
        );
      }

      const locationId = resolveBookingLocationId();
      if (!locationId) {
        throw new Error("Failed to resolve booking location for availability");
      }

      const startDate = new Date(currentDate);
      if (Number.isNaN(startDate.getTime())) {
        throw new Error("Invalid booking date");
      }

      const searchDays = Math.min(Math.max(Number(days) || 5, 1), 30);
      const searchEnd = new Date(startDate);
      searchEnd.setDate(searchEnd.getDate() + searchDays);

      const monthCache = new Map();
      const loadMonthAvailability = async (dateObj) => {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const monthKey = `${year}-${month}`;

        if (monthCache.has(monthKey)) {
          return monthCache.get(monthKey);
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/bookings/available-slots-month?staffId=${staff?._id || staffId}&locationId=${locationId}&month=${monthKey}&serviceDuration=${queryServiceDuration}&fresh=1`,
        );

        if (!response.ok) {
          throw new Error("Failed to fetch next available slots");
        }

        const monthData = await response.json();
        monthCache.set(monthKey, monthData);
        return monthData;
      };

      const collectedSlots = [];
      const cursor = new Date(startDate);

      while (cursor <= searchEnd && collectedSlots.length < 6) {
        const year = cursor.getFullYear();
        const month = String(cursor.getMonth() + 1).padStart(2, "0");
        const day = String(cursor.getDate()).padStart(2, "0");
        const dateKey = `${year}-${month}-${day}`;
        const monthData = await loadMonthAvailability(cursor);
        const daySlots = monthData?.days?.[dateKey]?.slots || [];

        for (const slot of daySlots) {
          const time24 = slot.time24 || slot.start || "";
          collectedSlots.push({
            date: dateKey,
            displayDate: slot.displayDate || formatDisplayDate(dateKey),
            time24,
            time12: slot.time12 || formatTime12(time24),
            endTime: slot.endTime || slot.end || "",
            available: true,
          });

          if (collectedSlots.length >= 6) {
            break;
          }
        }

        cursor.setDate(cursor.getDate() + 1);
      }

      setNextAvailableError("");
      setNextAvailableSlots(collectedSlots);
    } catch (err) {
      setNextAvailableError(err?.message || "Unable to fetch next available slots");
      setNextAvailableSlots([]);
    }
  };

  const fetchRecommendedProducts = async () => {
    try {
      if (!serviceId || !staffId) {
        return;
      }

      // Wait for staff state to be available
      if (!staff) {
        return;
      }
      // For freelancers, try multiple shop ID sources
      const shopId =
        staff?.shopId?._id ||
        staff?.activeLocationId?._id ||
        staff?.shopIds?.[0]?._id ||
        staff?.shopIds?.[0];
      if (!shopId) {
        return; // Don't error, just skip products for now
      }

      const url = `${process.env.REACT_APP_API_BASE_URL}/api/bookings/recommended-products/${serviceId}?shopId=${shopId}`;
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setRecommendedProducts(data.products || []);
      } else {
        setRecommendedProducts([]);
      }
    } catch (err) {
      setRecommendedProducts([]);
    }
  };

  const calculateTotal = () => {
    if (!service) return 0;

    let subtotal = effectiveServicePrice || service.price;

    // Add selected products
    const productsTotal = selectedProducts.reduce(
      (sum, p) => sum + p.price * p.quantity,
      0,
    );
    subtotal += productsTotal;

    // Add travel fee if mobile service selected
    if (serviceLocation === "client" && staff?.travelFee) {
      subtotal += staff.travelFee;
    }

    // Tip is always calculated on service value only (never travel fee/products).
    // A custom tip is a flat amount the customer typed; a preset is a percentage.
    const tipAmount = tipIsCustom
      ? Math.max(0, parseCustomTip() || 0)
      : ((effectiveServicePrice || service?.price || 0) * tipPercentage) / 100;
    const taxAmount =
      taxPreview.calculatedAtCheckout === false
        ? Number(taxPreview.taxAmount || 0)
        : 0;

    return (subtotal + tipAmount + taxAmount).toFixed(2);
  };

  const toggleProduct = (product, selectedVariant = null) => {
    const selectedProductId = selectedVariant?._id || product._id;
    const selectedPrice =
      typeof selectedVariant?.price === "number"
        ? selectedVariant.price
        : product.price || product.masterProductId?.basePrice || 0;
    const baseProductName = product.masterProductId?.name || product.name;
    const selectedVariantAttributes = Array.isArray(selectedVariant?.variantAttributes)
      ? selectedVariant.variantAttributes.map((attribute) => ({ ...attribute }))
      : [];
    const primaryVariantAttribute = selectedVariantAttributes[0] || null;
    const variantType = String(
      selectedVariant?.variantType ||
        primaryVariantAttribute?.dimensionName ||
        primaryVariantAttribute?.dimensionKey ||
        primaryVariantAttribute?.name ||
        primaryVariantAttribute?.type ||
        "",
    ).trim();
    const variantValue = String(
      primaryVariantAttribute?.label || primaryVariantAttribute?.value || "",
    ).trim();
    const variantDisplayName =
      selectedVariant?.variantName ||
      selectedVariant?.variantLabel ||
      variantValue ||
      "";
    const selectedName = variantDisplayName
      ? `${baseProductName} - ${variantDisplayName}`
      : baseProductName;
    const selectedStripeTaxCode =
      selectedVariant?.stripeTaxCode || product.stripeTaxCode;

    const existing = selectedProducts.find(
      (p) => p.productId === selectedProductId,
    );
    if (existing) {
      setSelectedProducts(
        selectedProducts.filter((p) => p.productId !== selectedProductId),
      );
    } else {
      const isFirstProduct = selectedProducts.length === 0;
      setSelectedProducts([
        ...selectedProducts,
        {
          productId: selectedProductId,
          familyProductId: product._id,
          quantity: 1,
          price: selectedPrice,
          name: selectedName,
          stripeTaxCode: selectedStripeTaxCode,
          variantKey: selectedVariant?._id || null,
          variantLabel: variantDisplayName || "",
          variantType,
          variantValue,
          variantAttributes: selectedVariantAttributes,
        },
      ]);
      if (isFirstProduct) {
        focusBillingAddressForProducts("products");
      }
    }
  };

  const updateProductQuantity = (productId, quantity) => {
    if (quantity < 1) return;
    setSelectedProducts(
      selectedProducts.map((p) =>
        p.productId === productId ? { ...p, quantity } : p,
      ),
    );
  };

  const focusBillingAddressForProducts = (
    returnTarget = BOOKING_BILLING_ADDRESS_RETURN_TARGET.PRODUCTS,
  ) => {
    setBillingAddressReturnTarget(getBillingAddressReturnTarget(returnTarget));
    window.requestAnimationFrame(() => {
      if (billingAddressSectionRef.current) {
        billingAddressSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      window.setTimeout(() => {
        billingStreetInputRef.current?.focus();
      }, 150);
    });
  };

  const scrollToSection = (sectionRef) => {
    if (sectionRef === customerInfoSectionRef) {
      setExpandedSection("customerInfo");
    } else if (sectionRef === serviceLocationSectionRef) {
      setExpandedSection("serviceLocation");
    } else if (sectionRef === dateTimeSectionRef) {
      setExpandedSection("dateTime");
    } else if (sectionRef === serviceCardRef) {
      // The service card only renders outside the summary step, so leave the
      // summary to reveal the option picker before scrolling to it.
      setExpandedSection("dateTime");
    } else if (
      sectionRef === productsSectionRef ||
      sectionRef === billingAddressSectionRef
    ) {
      setExpandedSection("products");
    } else if (sectionRef === summarySectionRef) {
      setExpandedSection("summary");
      setSummaryReturnSection("");
    }

    sectionRef?.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  useEffect(() => {
    if (selectedTime && showServiceSelector) {
      setShowServiceSelector(false);
    }
  }, [selectedTime, showServiceSelector]);

  const handleJoinWaitlist = async ({ replaceExistingWaitlist = false } = {}) => {
    const token = localStorage.getItem("ogestyle_token");
    const customerName =
      `${customerInfo.firstName || ""} ${customerInfo.lastName || ""}`.trim();
    const sameDayWaitlistMessage =
      "Same day waitlist is not available. Please choose another date and time.";
    const today = new Date();
    const todayYmd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    const selectedDateYmd = selectedDate
      ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
      : null;

    if (selectedDateYmd === todayYmd) {
      setWaitlistMessage({
        type: "error",
        text: sameDayWaitlistMessage,
      });
      return;
    }

    if (!token && (!customerName || !customerInfo.email)) {
      setWaitlistMessage({
        type: "error",
        text: "Please provide your name and email to join the waitlist.",
      });
      return;
    }

    const shopId = staff?.shopId?._id || staff?.shopId;
    if (!shopId || !service?._id || !staff?._id) {
      setWaitlistMessage({
        type: "error",
        text: "Missing required booking context for waitlist.",
      });
      return;
    }

    if (!["R3", "R4", "R7"].includes(staff.role)) {
      setWaitlistMessage({
        type: "error",
        text: "Waitlist is currently available for active professionals only.",
      });
      return;
    }

    setWaitlistSubmitting(true);
    setWaitlistMessage({ type: "", text: "" });
    setActiveWaitlistConflict(null);
    if (replaceExistingWaitlist) {
      setPendingWaitlistReplacement(null);
    }

    try {
      const dateStr = selectedDate
        ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
        : null;

      const waitlistServiceBundle = bundleServices.map((svc, index) => {
        const svcKey = String(svc?._id || "");
        const lineSelection =
          index === 0 ? serviceSelection : addOnSelections[svcKey] || {};
        const line = resolveServicePricing(svc, lineSelection);

        return {
          serviceId: svcKey,
          name: String((line && line.label) || svc?.name || "Service"),
          durationMinutes:
            Number(
              (line && line.totalMinutes) || normalizeServiceDuration(svc),
            ) || 0,
          price:
            Number((line && line.totalPrice) || normalizeServicePrice(svc)) || 0,
          selection: lineSelection,
        };
      });

      const payload = {
        shopId,
        staffId: staff._id,
        serviceId: service._id,
        preferredDates: dateStr ? [dateStr] : [],
        preferredTimeSlots: [],
        flexibleDates: !dateStr,
        flexibleStaff: false,
        customerName,
        customerFirstName: customerInfo.firstName,
        customerLastName: customerInfo.lastName,
        customerEmail: customerInfo.email,
        customerPhone: customerInfo.phone,
        serviceBundle: waitlistServiceBundle,
        totalServiceDurationMinutes: effectiveBundleDurationMinutes,
        totalServicePrice: Number(effectiveServicePrice || normalizeServicePrice(service) || 0) || 0,
        // F2: the server re-resolves this and owns the request's duration/price.
        selection: {
          quantity: serviceQuantity,
          hours: serviceHours,
          tierId: serviceTierId,
        },
        bookingContext: {
          source: "freelancer-booking",
          role: staff.role || null,
          selectedDate: dateStr,
          groupSize: 1,
        },
        notes: dateStr
          ? `Requested from booking flow for preferred date ${dateStr}`
          : "Requested from booking flow without a specific date",
      };

      payload.waitDurationDays = waitlistDurationDays;
      if (replaceExistingWaitlist) {
        payload.replaceExistingWaitlist = true;
      }

      const requestHeaders = {
        "Content-Type": "application/json",
      };

      if (token) {
        requestHeaders.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/waitlist`,
        {
          method: "POST",
          headers: requestHeaders,
          body: JSON.stringify(payload),
        },
      );

      const data = await response.json();
      if (!response.ok) {
        if (response.status === 409 && data?.error === "waitlist_replacement_required") {
          setPendingWaitlistReplacement(data.existingWaitlist || {});
          setWaitlistMessage({
            type: "warning",
            text:
              data.message ||
              "You already have a waitlist request for this provider and date.",
          });
          return;
        }
        if (response.status === 409 && data?.error === "waitlist_cooldown_active") {
          setPendingWaitlistReplacement(null);
          setActiveWaitlistConflict(data.existingWaitlist || {});
          setWaitlistMessage({
            type: "warning",
            text:
              data.message ||
              "You already have an active waitlist request within 21 days of this requested date.",
          });
          return;
        }
        throw new Error(data.message || data.error || "Failed to join waitlist");
      }

      setPendingWaitlistReplacement(null);
      setActiveWaitlistConflict(null);
      setWaitlistMessage({
        type: "success",
        text:
          data?.message ||
          "Waitlist request saved. Your waitlist is not active or guaranteed until you book an appointment.",
      });
      setWaitlistJoined(true);
    } catch (err) {
      setWaitlistMessage({
        type: "error",
        text: err.message || "Unable to join waitlist right now.",
      });
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const handleKeepExistingWaitlist = () => {
    setPendingWaitlistReplacement(null);
    setWaitlistMessage({
      type: "success",
      text: "Your existing waitlist request was kept unchanged. You can continue booking.",
    });
  };

  const waitlistToday = new Date();
  const waitlistTodayYmd = `${waitlistToday.getFullYear()}-${String(waitlistToday.getMonth() + 1).padStart(2, "0")}-${String(waitlistToday.getDate()).padStart(2, "0")}`;
  const selectedDateYmd = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
    : null;
  const isSelectedDateToday = selectedDateYmd === waitlistTodayYmd;
  const sameDayWaitlistMessage =
    "Same day waitlist is not available. Please choose another date and time.";

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (itemCount > 0 && !cartWarningAcknowledged) {
      setShowCartWarningModal(true);
      return;
    }

    // Check authentication first (but allow guest checkout)
    if (!isAuthenticated && !hasChosenGuest) {
      setShowAuthModal(true);
      return;
    }

    // Only validate date/time selection (HTML5 validation handles form fields with required attributes)
    if (!selectedDate || !selectedTime) {
      setError("Please select a date and time for your appointment");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Backstop for the live tip validation - a hidden button is not a closed API.
    const tipProblem = customTipErrorFor(
      effectiveServicePrice || service?.price || 0,
    );
    if (tipProblem) {
      setError(tipProblem);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const holdIdentity = {
      shopId: resolveBookingLocationId(),
      staffId,
      serviceId,
      appointmentDate: selectedDate,
      startTime: selectedTime,
    };
    // ADVISORY only: this timer lives in sessionStorage, so it reserves nothing on the
    // server and must never block a booking the server would accept. See the same note
    // in CustomerBooking.js.
    if (!getDirectBookingHold(holdIdentity)) {
      setSlotInvalidatedNotice(
        "This time may have been taken while you were completing the form. We will confirm it when you continue to payment.",
      );
    }

    if (
      waitlistCompatibility?.hasPendingWaitlist &&
      waitlistCompatibility.compatible === false &&
      !waitlistMismatchAcknowledged
    ) {
      setError(
        "Please acknowledge that continuing will cancel your pending waitlist request.",
      );
      waitlistMismatchSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }

    if (
      serviceLocation === "client" &&
      (!clientAddress.street || !clientAddress.city || !travelFeeAccepted)
    ) {
      setError(
        "Please provide complete address and accept travel charges for mobile service",
      );
      return;
    }

    if (serviceLocation === "client" && staff?.role === "R4") {
      try {
        const distanceCheckResponse = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/bookings/mobile-distance-check`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(localStorage.getItem("ogestyle_token") && {
                Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}`,
              }),
            },
            body: JSON.stringify({
              staffId,
              serviceLocation,
              clientAddress,
            }),
          },
        );

        const distanceCheckData = await distanceCheckResponse.json();
        if (!distanceCheckResponse.ok || !distanceCheckData?.withinRange) {
          setError(
            distanceCheckData?.message ||
              "This address is outside the professional's mobile service area.",
          );
          return;
        }
      } catch (distanceErr) {
        setError(
          "Unable to verify mobile service distance right now. Please try again.",
        );
        return;
      }
    }

    setPaymentProcessing(true);

    try {
      if (
        isBillingAddressRequired(selectedProducts) &&
        (!customerAddress.street ||
          !customerAddress.city ||
          !customerAddress.state ||
          !customerAddress.zipCode ||
          !customerAddress.country)
      ) {
        setError(
          "Billing address is required when products are included in booking checkout",
        );
        focusBillingAddressForProducts("summary");
        setPaymentProcessing(false);
        return;
      }
      // Calculate end time based on service duration
      const calculateEndTime = (startTime, durationMinutes) => {
        const [hours, minutes] = startTime.split(":").map(Number);
        const totalMinutes = hours * 60 + minutes + durationMinutes;
        const endHours = Math.floor(totalMinutes / 60);
        const endMins = totalMinutes % 60;
        return `${String(endHours).padStart(2, "0")}:${String(endMins).padStart(2, "0")}`;
      };

      const bookingData = {
        // No sensitive IDs - backend will resolve internally
        staffId,
        serviceId,
        appointmentDate: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`,
        startTime: selectedTime,
        endTime: calculateEndTime(selectedTime, effectiveBundleDurationMinutes),
        customerTimezone:
          Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago",
        firstName: customerInfo.firstName,
        lastName: customerInfo.lastName,
        email: customerInfo.email,
        phone: customerInfo.phone,
        smsConsent: isAuthenticated ? true : smsConsent, // Authenticated users assumed opted-in, guests must check box
        customerName:
          `${customerInfo.firstName || ""} ${customerInfo.lastName || ""}`.trim() ||
          "Customer",
        customerAddress:
          selectedProducts.length > 0
            ? {
                street: customerAddress.street,
                city: customerAddress.city,
                state: customerAddress.state,
                zipCode: customerAddress.zipCode,
                country: customerAddress.country,
              }
            : null,
        products: selectedProducts.map((p) => ({
          productId: p.productId,
          quantity: p.quantity,
          variantKey: p.variantKey || null,
          variantLabel: p.variantLabel || "",
          variantType: p.variantType || "",
          variantValue: p.variantValue || "",
          variantAttributes: p.variantAttributes || [],
        })),
        // A custom tip has no percentage. Sending 0 makes the server use the flat amount.
        tipPercentage: tipIsCustom ? 0 : tipPercentage,
        tipAmount: tipIsCustom
          ? Math.max(0, parseCustomTip() || 0)
          : ((effectiveServicePrice || service?.price || 0) * tipPercentage) / 100,
        groupSize: 1,
        groupMembers: [],
        packagePurchaseId: null,
        usePackageCredit: false,
        bookingType: "online",
        paymentMethod: "stripe",
        promoCode: null,
        promoDiscount: 0,
        serviceLocation,
        clientAddress: serviceLocation === "client" ? clientAddress : null,
        travelFee: serviceLocation === "client" ? staff?.travelFee || 0 : 0,
        travelFeeAccepted:
          serviceLocation === "client" ? travelFeeAccepted : false,
        lookId: lookId || null,
        totalAmount: calculateTotal(),
        serviceBundle: bundleServices.map((svc, index) => {
          const svcKey = String(svc._id);
          const isPrimary = index === 0;
          const lineSelection = isPrimary
            ? serviceSelection
            : addOnSelections[svcKey] || {};
          const line = resolveServicePricing(svc, lineSelection);

          return {
            serviceId: svc._id,
            name: (line && line.label) || svc.name,
            durationMinutes:
              (line && line.totalMinutes) || normalizeServiceDuration(svc),
            price: (line && line.totalPrice) || normalizeServicePrice(svc),
            isPrimary,
            // P6: the server reads a per-line `selection` for every entry.
            selection: lineSelection,
          };
        }),
        serviceBundleCount: bundleServices.length,
        serviceBundleDuration: effectiveBundleDurationMinutes,
        serviceBundlePrice: effectiveServicePrice || service.price,
        // Optional note for the professional (validated server-side)
        customerNotes: customerNote.trim(),
        isFreelancerBooking: true,
        isTestBooking: isPreviewMode, // Mark as test booking if in preview mode
        waitlistMismatchAcknowledged:
          waitlistCompatibility?.hasPendingWaitlist &&
          waitlistCompatibility.compatible === false &&
          waitlistMismatchAcknowledged,
        // C1: audit - when the customer accepted the cancellation/refund policy
        policyAcceptedAt: policyAccepted ? new Date().toISOString() : null,
      };
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/bookings/customer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(localStorage.getItem("ogestyle_token") && {
              Authorization: `Bearer ${localStorage.getItem("ogestyle_token")}`,
            }),
          },
          body: JSON.stringify(bookingData),
        },
      );
      if (response.ok) {
        const result = await response.json();

        // TEST BOOKING: No payment needed, redirect to confirmation page
        if (result.isTestBooking) {
          navigate(
            `/booking-confirmation?booking_id=${result.booking.id}&test=true`,
            {
              state: {
                testBooking: true,
                booking: result.booking,
                message: result.message,
              },
            },
          );
          return;
        }

        // Redirect to Stripe checkout
        if (result.stripeCheckoutUrl) {
          window.location.href = result.stripeCheckoutUrl;
        } else {
          setError("Payment setup failed. Please try again.");
          setPaymentProcessing(false);
        }
      } else {
        const errorData = await response.json();
        // The server decides whether the slot is free. When it reports the time was
        // taken, return the customer to a REFRESHED grid with a clear message instead of
        // leaving a stale slot on screen. Nothing is charged - Stripe is only reached
        // after this check passes.
        if (
          errorData.error === "time_slot_unavailable" ||
          errorData.error === "slot_unavailable" ||
          errorData.error === "time_not_available"
        ) {
          clearDirectBookingHold({
            shopId: resolveBookingLocationId(),
            staffId,
            serviceId,
            appointmentDate: selectedDate,
            startTime: selectedTime,
          });
          setSelectedTime("");
          setSlotInvalidatedNotice(
            buildSlotUnavailableNotice(errorData.code, errorData.message),
          );
          setError("");
          await fetchAvailableSlots();
          window.scrollTo({ top: 0, behavior: "smooth" });
          setPaymentProcessing(false);
          return;
        }

        setError(errorData.message || "Failed to create booking");
        setPaymentProcessing(false);
      }
    } catch (err) {
      setError("An error occurred while creating your booking");
      setPaymentProcessing(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          fontSize: isMobile ? "12px" : "16px",
        }}
      >
        Loading booking information...
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          color: "#dc3545",
          fontSize: isMobile ? "12px" : "16px",
        }}
      >
        {error}
      </div>
    );
  }

  const customerInfoComplete = Boolean(
    customerInfo.firstName?.trim() &&
      customerInfo.lastName?.trim() &&
      customerInfo.email?.trim() &&
      customerInfo.phone?.trim() &&
      (isAuthenticated || smsConsent),
  );
  const mobileAddressComplete = Boolean(
    clientAddress.street?.trim() &&
      clientAddress.city?.trim() &&
      clientAddress.state?.trim() &&
      clientAddress.zip?.trim(),
  );
  const serviceLocationComplete =
    serviceLocation === "freelancer" ||
    (serviceLocation === "client" && mobileAddressComplete && travelFeeAccepted);
  const dateTimeComplete = Boolean(selectedDate && selectedTime);
  const scheduleComplete = dateTimeComplete;
  const billingAddressComplete =
    !isBillingAddressRequired(selectedProducts) ||
    Boolean(
      customerAddress.street?.trim() &&
        customerAddress.city?.trim() &&
        customerAddress.state?.trim() &&
        customerAddress.zipCode?.trim() &&
        customerAddress.country?.trim(),
    );
  const summaryReady =
    customerInfoComplete &&
    serviceLocationComplete &&
    scheduleComplete &&
    billingAddressComplete;
  const billingAddressRequired = isBillingAddressRequired(selectedProducts);
  const waitlistOnlyPath = false;
  const waitlistContactReady = Boolean(
    `${customerInfo.firstName || ""} ${customerInfo.lastName || ""}`.trim() &&
      customerInfo.email?.trim(),
  );
  const summaryBlockers = buildSummaryBlockers({
    customerInfoComplete,
    serviceLocationComplete,
    scheduleComplete,
    billingAddressRequired,
    billingAddressComplete,
    customerInfoSectionRef,
    serviceLocationSectionRef,
    dateTimeSectionRef,
    productsSectionRef,
  });
  const summarySoftWarnings = buildSummarySoftWarnings({
    selectedProducts,
    waitlistOnlyPath,
    serviceLocation,
    travelFee: staff?.travelFee || 0,
  });
  const hasProductsStep = Boolean(
    recommendedProducts && recommendedProducts.length > 0,
  );
  const showFinalCheckoutStep = summaryReady && expandedSection === "summary";
  const waitlistMismatchRequiresAcknowledgment = Boolean(
    waitlistCompatibility?.hasPendingWaitlist &&
      waitlistCompatibility.compatible === false,
  );
  const submitBlockers = [
    ...summaryBlockers,
    // F2/P2: a choice-based line cannot be priced until the customer chooses.
    primarySelectionPending
      ? {
          key: "pricing-selection",
          label: `Choose an option for ${selectionPendingLabel}`,
          ref: serviceCardRef,
        }
      : null,
    showFinalCheckoutStep && !policyAccepted
      ? {
          key: "policy",
          label: "Accept the Cancellation & Reschedule Policy",
          ref: policySectionRef,
          requiresPolicyFocus: true,
        }
      : null,
    showFinalCheckoutStep &&
    waitlistMismatchRequiresAcknowledgment &&
    !waitlistMismatchAcknowledged
      ? {
          key: "waitlist-mismatch",
          label: "Acknowledge waitlist cancellation",
          ref: waitlistMismatchSectionRef,
        }
      : null,
  ].filter(Boolean);
  const sectionSequence = [
    "customerInfo",
    "serviceLocation",
    "dateTime",
    ...(hasProductsStep ? ["products"] : []),
    "summary",
  ];
  const advanceToNextSection = (sectionKey) => {
    const currentIndex = sectionSequence.indexOf(sectionKey);
    if (currentIndex === -1 || currentIndex >= sectionSequence.length - 1) {
      return;
    }
    setExpandedSection(sectionSequence[currentIndex + 1]);
  };
  const getPreviousSectionKey = (sectionKey) => {
    const currentIndex = sectionSequence.indexOf(sectionKey);
    if (currentIndex <= 0) {
      return "";
    }
    return sectionSequence[currentIndex - 1];
  };
  const goToPreviousSection = (sectionKey) => {
    const previousKey = getPreviousSectionKey(sectionKey);
    if (previousKey) {
      setExpandedSection(previousKey);
    }
  };
  const nextButtonStyle = {
    padding: "8px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    background: "#fff",
    color: "#1f2937",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
  };

  const handleSummaryBlockerClick = (blocker) => {
    if (blocker.requiresBillingAddressFocus) {
      setSummaryReturnSection("products");
      focusBillingAddressForProducts("summary");
      return;
    }

    setSummaryReturnSection(blocker.key);
    scrollToSection(blocker.ref);
  };
  const handleSubmitBlockerClick = (blocker) => {
    if (blocker.requiresPolicyFocus) {
      blocker.ref?.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      // The tick lives in the modal now, so send them straight to it.
      setShowPolicyModal(true);
      return;
    }

    handleSummaryBlockerClick(blocker);
  };

  return (
    <>
      <CustomerHeader />
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          padding: isMobile ? "16px" : "24px",
          fontFamily: "Arial, sans-serif",
          paddingTop: "100px", // Account for header
          minHeight: "100vh",
        }}
      >
        {/* Preview Mode Banner */}
        {isPreviewMode && (
          <div
            style={{
              backgroundColor: "#fef3c7",
              border: "2px solid #f59e0b",
              borderRadius: "12px",
              padding: "16px",
              marginBottom: "24px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <span style={{ fontSize: "24px" }}>🔍</span>
            <div>
              <div
                style={{
                  fontWeight: "600",
                  color: "#92400e",
                  fontSize: "15px",
                }}
              >
                Preview Mode - Test Booking
              </div>
              <div style={{ color: "#b45309", fontSize: "13px" }}>
                This is a test booking. No payment will be processed.
              </div>
            </div>
          </div>
        )}

        {/* Service Info */}
        {expandedSection !== "summary" && (
          <div
            ref={serviceCardRef}
            style={{
              backgroundColor: "#f8f9fa",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
              border: "1px solid #e9ecef",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "12px" : "16px",
                fontWeight: "700",
                marginBottom: "4px",
              }}
            >
              {service?.name}
            </div>
            <div
              style={{
                fontSize: "13px",
                opacity: 0.95,
                marginBottom: "8px",
              }}
            >
              {service?.description}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "#2d6da3",
                fontWeight: "600",
              }}
            >
              {/* F2: hidden until the customer chooses, because the stored
                  price is the lowest option and the duration the longest. */}
              {/* P4: this line describes the WHOLE bundle - it is the one that
                  says "2 services" - so it must show the bundle totals, not the
                  primary's own price and duration. */}
              {!primarySelectionPending && (
                <>
                  ${(effectiveServicePrice || service?.price || 0).toFixed(2)} •{" "}
                  {effectiveBundleDurationMinutes ||
                    normalizeServiceDuration(service)}{" "}
                  minutes
                  {bundleServiceCount > 1
                    ? ` • ${bundleServiceCount} services`
                    : ""}
                </>
              )}
            </div>

            {/* F1/F2: choice-based pricing (per-unit quantity, hours, option) */}
            <ServicePricingSelector
              service={primaryBundleService}
              selection={serviceSelection}
              onChange={handleServiceSelectionChange}
              formatPrice={(value) => `$${Number(value || 0).toFixed(2)}`}
              isMobile={isMobile}
              accentColor="#2d6da3"
            />

            <div style={{ marginTop: "10px", fontSize: "12px", color: "#64748b" }}>
              {primaryBundleService && (
                <div
                  key={`${primaryBundleService._id}-primary`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    marginBottom: "10px",
                  }}
                >
                  <div>
                    <div style={{ fontWeight: "700", color: "#0f172a" }}>
                      Primary: {primaryDisplayName}
                    </div>
                    <div style={{ fontSize: "12px", color: "#6b7280" }}>
                      {primarySelectionPending
                        ? "Choose an option above"
                        : `${displayedPrimaryDuration} min • $${(
                            displayedPrimaryPrice || 0
                          ).toFixed(2)}`}
                    </div>
                  </div>
                </div>
              )}

              {hasAdditionalBundleServices && (
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                  <button
                    type="button"
                    onClick={handleManageAdditionalServices}
                    style={{
                      border: "1px solid #c7d2fe",
                      background: "#eef2ff",
                      color: "#3730a3",
                      borderRadius: "999px",
                      padding: "4px 10px",
                      fontSize: "12px",
                      fontWeight: "700",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {serviceBundle.length > 0 ? "Change service" : "Add additional service"}
                  </button>
                </div>
              )}

              {addonBundleServices.length > 0 && (
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "700",
                    color: "#0f172a",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    marginBottom: "8px",
                  }}
                >
                  Service bundle
                </div>
              )}
              {addonBundleServices.map((svc, idx) => {
                const addOnSelection = addOnSelections[String(svc._id)] || {};
                const addOnLine = resolveServicePricing(svc, addOnSelection);
                const addOnNeedsChoice = Boolean(
                  addOnLine && addOnLine.requiresSelection,
                );

                return (
                  <div
                    key={`${svc._id}-${idx}`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                      padding: "10px 12px",
                      borderRadius: "10px",
                      background: "#ffffff",
                      border: "1px solid #e5e7eb",
                      marginBottom: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "12px",
                      }}
                    >
                      <div style={{ fontWeight: "700", color: "#0f172a" }}>
                        Add-on: {(addOnLine && addOnLine.label) || svc.name}
                      </div>
                      <div style={{ fontSize: "12px", color: "#6b7280" }}>
                        {addOnNeedsChoice
                          ? "Choose an option"
                          : `${(addOnLine && addOnLine.totalMinutes) ?? normalizeServiceDuration(svc)} min • $${Number(
                              (addOnLine && addOnLine.totalPrice) ??
                                normalizeServicePrice(svc),
                            ).toFixed(2)}`}
                      </div>
                    </div>
                    {/* P2 (F3): each add-on owns its own units / hours / option. */}
                    <ServicePricingSelector
                      service={svc}
                      selection={addOnSelection}
                      onChange={(patch) =>
                        handleAddOnSelectionChange(svc._id, patch)
                      }
                      formatPrice={(value) =>
                        `$${Number(value || 0).toFixed(2)}`
                      }
                      isMobile={isMobile}
                      accentColor="#2d6da3"
                    />
                  </div>
                );
              })}

              {showServiceSelector && hasAdditionalBundleServices && !selectedTime && (
                <div
                  style={{
                    backgroundColor: "#f8fafc",
                    padding: "14px 16px",
                    borderRadius: "8px",
                    marginTop: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      fontWeight: "700",
                      color: "#334155",
                      marginBottom: "8px",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Additional services
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#92400e",
                      background: "#fff7ed",
                      border: "1px solid #fed7aa",
                      borderRadius: "8px",
                      padding: "8px 10px",
                      marginBottom: "10px",
                    }}
                  >
                    Selecting a different service may change the already selected time.
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "repeat(2, minmax(0, 1fr))"
                        : "repeat(4, minmax(0, 1fr))",
                      gap: "10px",
                    }}
                  >
                    {selectableBundleServices
                      .filter((svc) => String(svc?._id) !== String(service?._id))
                      .map((svc) => {
                        const isChecked = serviceBundle.some(
                          (bundleSvc) => String(bundleSvc._id) === String(svc._id),
                        );

                        return (
                          <label
                            key={svc._id}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "8px",
                              padding: "10px",
                              borderRadius: "8px",
                              border: isChecked ? "1px solid #2563eb" : "1px solid #cbd5e1",
                              background: isChecked ? "#eff6ff" : "#ffffff",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleBundleService(svc)}
                              style={{ marginTop: "2px" }}
                            />
                            <span style={{ fontSize: "12px", color: "#0f172a", fontWeight: "600" }}>
                              {svc.name}
                              <span style={{ display: "block", color: "#6b7280", fontWeight: "500" }}>
                                {normalizeServiceDuration(svc)} min • ${normalizeServicePrice(svc).toFixed(2)}
                              </span>
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>


          </div>
        )}

        <form onSubmit={handleSubmit}>
          

          {/* Customer Information - Only show for guest users */}
          {!isAuthenticated && (
            <section
              ref={customerInfoSectionRef}
              style={{
                marginBottom: "14px",
                padding: "16px",
                borderRadius: "8px",
                ...getSectionBlockStyle(
                  customerInfoComplete ? "complete" : "attention",
                ),
                ...getCollapsibleSectionStyle(expandedSection, "customerInfo"),
              }}
            >
              <SectionStatusHeader
                title="Your Information"
                state={customerInfoComplete ? "complete" : "attention"}
                isMobile={isMobile}
                hideTopControls
                sectionExpanded={expandedSection === "customerInfo"}
              />
              <SummaryReturnPrompt
                summaryReturnSection={summaryReturnSection}
                sectionKey="customerInfo"
                onBackToSummary={() => {
                  setSummaryReturnSection("");
                  scrollToSection(summarySectionRef);
                }}
              />
              {!customerInfoComplete && (
                <p style={{ margin: "0 0 10px 0", color: "#92400e", fontSize: "12px" }}>
                  Needs attention: complete all contact fields and accept SMS consent to continue.
                </p>
              )}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <input
                  type="text"
                  placeholder="First Name"
                  value={customerInfo.firstName}
                  onChange={(e) =>
                    setCustomerInfo((prev) => ({
                      ...prev,
                      firstName: e.target.value,
                    }))
                  }
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: isMobile ? "12px" : "13px",
                    fontFamily: "inherit",
                  }}
                  required
                />
                <input
                  type="text"
                  placeholder="Last Name"
                  value={customerInfo.lastName}
                  onChange={(e) =>
                    setCustomerInfo((prev) => ({
                      ...prev,
                      lastName: e.target.value,
                    }))
                  }
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: isMobile ? "12px" : "13px",
                    fontFamily: "inherit",
                  }}
                  required
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                  gap: "16px",
                }}
              >
                <input
                  type="email"
                  placeholder="Email"
                  value={customerInfo.email}
                  onChange={(e) =>
                    setCustomerInfo((prev) => ({
                      ...prev,
                      email: e.target.value,
                    }))
                  }
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: isMobile ? "12px" : "13px",
                    fontFamily: "inherit",
                  }}
                  required
                />
                <input
                  type="tel"
                  placeholder="Phone"
                  value={customerInfo.phone}
                  onChange={(e) =>
                    setCustomerInfo((prev) => ({
                      ...prev,
                      phone: e.target.value,
                    }))
                  }
                  style={{
                    padding: "12px",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    fontSize: isMobile ? "12px" : "13px",
                    fontFamily: "inherit",
                  }}
                  required
                />
              </div>

              <CustomerNoteField
                value={customerNote}
                onChange={setCustomerNote}
                maxLength={500}
                isMobile={isMobile}
              />

              {/* SMS Consent Checkbox (TCPA Compliance) */}
              <div
                style={{
                  marginTop: "16px",
                  padding: "16px",
                  background: "#f9fafb",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "12px",
                    cursor: "pointer",
                    fontSize: "14px",
                    lineHeight: "1.6",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    required
                    style={{
                      marginTop: "3px",
                      width: "18px",
                      height: "18px",
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  />
                  <span style={{ color: "#374151" }}>
                    I agree to receive SMS notifications from OGESTYLE about my
                    appointments, including booking confirmations, reminders,
                    and updates. Message frequency may vary. Message and data
                    rates may apply. Text HELP for support. Reply STOP to
                    opt-out anytime.{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#2563eb",
                        textDecoration: "underline",
                      }}
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>
              </div>
              <div
                style={{
                  marginTop: "14px",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  style={{
                    ...nextButtonStyle,
                    visibility: "hidden",
                  }}
                  disabled
                >
                  Back
                </button>
                <button
                  type="button"
                  style={{
                    ...nextButtonStyle,
                    opacity: customerInfoComplete ? 1 : 0.6,
                    cursor: customerInfoComplete ? "pointer" : "not-allowed",
                  }}
                  disabled={!customerInfoComplete}
                  onClick={() => advanceToNextSection("customerInfo")}
                >
                  Next
                </button>
              </div>
            </section>
          )}

          {/* Service Location */}
          <section
            ref={serviceLocationSectionRef}
            style={{
              marginBottom: "14px",
              padding: "16px",
              borderRadius: "8px",
              ...getSectionBlockStyle(
                serviceLocationComplete ? "complete" : "attention",
              ),
              ...getCollapsibleSectionStyle(expandedSection, "serviceLocation"),
            }}
          >
            <SectionStatusHeader
              title="Service Location"
              state={serviceLocationComplete ? "complete" : "attention"}
              isMobile={isMobile}
              hideTopControls
              sectionExpanded={expandedSection === "serviceLocation"}
            />
            <SummaryReturnPrompt
              summaryReturnSection={summaryReturnSection}
              sectionKey="serviceLocation"
              onBackToSummary={() => {
                setSummaryReturnSection("");
                scrollToSection(summarySectionRef);
              }}
            />
            {!serviceLocationComplete && (
              <p style={{ margin: "0 0 10px 0", color: "#92400e", fontSize: "12px" }}>
                Needs attention: complete mobile address and accept travel fee for at-home service.
              </p>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : staff?.travelFee
                    ? "1fr 1fr"
                    : "1fr",
                gap: "12px",
              }}
            >
              {/* Studio Option */}
              <div
                style={{
                  border:
                    serviceLocation === "freelancer"
                      ? "2px solid #2d6da3"
                      : "1px solid #e9ecef",
                  borderRadius: "8px",
                  padding: "16px",
                  cursor: "pointer",
                  backgroundColor:
                    serviceLocation === "freelancer" ? "#f0f8ff" : "#fff",
                }}
                onClick={() => setServiceLocation("freelancer")}
              >
                <label style={{ cursor: "pointer", display: "block" }}>
                  <input
                    type="radio"
                    name="serviceLocation"
                    value="freelancer"
                    checked={serviceLocation === "freelancer"}
                    onChange={(e) => setServiceLocation(e.target.value)}
                    style={{ marginRight: "12px" }}
                  />
                  <div style={{ display: "inline-block" }}>
                    <div
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        marginBottom: "4px",
                      }}
                    >
                      {staff?.role === "R7" && staff?.boothRentalLocation
                        ? `At ${staff.boothRentalLocation.businessName}`
                        : "At Freelancer's Studio"}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#666",
                        marginBottom: "4px",
                      }}
                    >
                      {staff?.role === "R7" && staff?.boothRentalLocation
                        ? `${staff.boothRentalLocation.street}, ${staff.boothRentalLocation.city}, ${staff.boothRentalLocation.state}`
                        : staff?.address}
                    </div>
                    <div
                      style={{
                        fontSize: "14px",
                        color: "#28a745",
                        fontWeight: "600",
                      }}
                    >
                      No travel charge
                    </div>
                  </div>
                </label>
              </div>

              {/* Mobile Service Option */}
              {staff?.travelFee && (
                <div
                  style={{
                    border:
                      serviceLocation === "client"
                        ? "2px solid #2d6da3"
                        : "1px solid #e9ecef",
                    borderRadius: "8px",
                    padding: "16px",
                    cursor: "pointer",
                    backgroundColor:
                      serviceLocation === "client" ? "#f0f8ff" : "#fff",
                  }}
                  onClick={() => setServiceLocation("client")}
                >
                  <label style={{ cursor: "pointer", display: "block" }}>
                    <input
                      type="radio"
                      name="serviceLocation"
                      value="client"
                      checked={serviceLocation === "client"}
                      onChange={(e) => setServiceLocation(e.target.value)}
                      style={{ marginRight: "12px" }}
                    />
                    <div style={{ display: "inline-block" }}>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "600",
                          marginBottom: "4px",
                        }}
                      >
                        At My Location (Mobile Service)
                      </div>
                      <div
                        style={{
                          fontSize: "14px",
                          color: "#2d6da3",
                          fontWeight: "600",
                        }}
                      >
                        Travel fee: ${staff.travelFee}
                      </div>
                      {staff?.maxTravelDistance && (
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#666",
                            marginTop: "4px",
                          }}
                        >
                          ⚠️ Travels up to {staff.maxTravelDistance} miles
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              )}
            </div>

            {serviceLocation === "client" && (
              <div style={{ marginTop: "16px" }}>
                {!isAuthenticated && (
                  <>
                    <h4
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#1a1a1a",
                        marginBottom: "16px",
                      }}
                    >
                      Service Address
                    </h4>

                    <div style={{ marginBottom: "16px" }}>
                      <AddressAutocompleteInput
                        value={clientAddress.street}
                        onChange={(val) =>
                          setClientAddress((prev) => ({ ...prev, street: val }))
                        }
                        onAddressSelect={({ street, city, state, zip }) =>
                          setClientAddress((prev) => ({
                            ...prev,
                            street: street || prev.street,
                            city: city || prev.city,
                            state: state || prev.state,
                            zip: zip || prev.zip,
                          }))
                        }
                        placeholder="Street Address"
                        required={serviceLocation === "client"}
                        inputStyle={{
                          width: "100%",
                          padding: "12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: isMobile ? "12px" : "13px",
                          fontFamily: "inherit",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <input
                        type="text"
                        placeholder="City"
                        value={clientAddress.city}
                        onChange={(e) =>
                          setClientAddress((prev) => ({
                            ...prev,
                            city: e.target.value,
                          }))
                        }
                        style={{
                          flex: "2",
                          minWidth: "120px",
                          padding: "12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: isMobile ? "12px" : "13px",
                          fontFamily: "inherit",
                        }}
                        required={serviceLocation === "client"}
                      />
                      <input
                        type="text"
                        placeholder="State"
                        value={clientAddress.state}
                        onChange={(e) =>
                          setClientAddress((prev) => ({
                            ...prev,
                            state: e.target.value,
                          }))
                        }
                        style={{
                          flex: "1",
                          minWidth: "80px",
                          padding: "12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: isMobile ? "12px" : "13px",
                          fontFamily: "inherit",
                        }}
                        required={serviceLocation === "client"}
                      />
                      <input
                        type="text"
                        placeholder="ZIP"
                        value={clientAddress.zip}
                        onChange={(e) =>
                          setClientAddress((prev) => ({
                            ...prev,
                            zip: e.target.value,
                          }))
                        }
                        style={{
                          flex: "1",
                          minWidth: "80px",
                          padding: "12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontSize: isMobile ? "12px" : "13px",
                          fontFamily: "inherit",
                        }}
                        required={serviceLocation === "client"}
                      />
                    </div>
                  </>
                )}

                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px",
                    backgroundColor: "#fff3cd",
                    border: "1px solid #ffeaa7",
                    borderRadius: "6px",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      fontSize: isMobile ? "11px" : "12px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={travelFeeAccepted}
                      onChange={(e) => setTravelFeeAccepted(e.target.checked)}
                      style={{ marginRight: "8px" }}
                      required={serviceLocation === "client"}
                    />
                    I understand and accept the travel fee of $
                    {staff?.travelFee || 0} for mobile service
                  </label>
                </div>
              </div>
            )}

            <div
              style={{
                marginTop: "14px",
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <button
                type="button"
                style={nextButtonStyle}
                onClick={() => goToPreviousSection("serviceLocation")}
              >
                Back
              </button>
              <button
                type="button"
                style={{
                  ...nextButtonStyle,
                  opacity: serviceLocationComplete ? 1 : 0.6,
                  cursor: serviceLocationComplete ? "pointer" : "not-allowed",
                }}
                disabled={!serviceLocationComplete}
                onClick={() => advanceToNextSection("serviceLocation")}
              >
                Next
              </button>
            </div>
          </section>

          {/* Date & Time Selection */}
          <section
            ref={dateTimeSectionRef}
            style={{
              marginBottom: "14px",
              padding: "16px",
              borderRadius: "8px",
              ...getSectionBlockStyle(scheduleComplete ? "complete" : "attention"),
              ...getCollapsibleSectionStyle(expandedSection, "dateTime"),
            }}
          >
            <SectionStatusHeader
              title="Select Date & Time"
              state={scheduleComplete ? "complete" : "attention"}
              isMobile={isMobile}
              hideTopControls
              sectionExpanded={expandedSection === "dateTime"}
            />
            <SummaryReturnPrompt
              summaryReturnSection={summaryReturnSection}
              sectionKey="dateTime"
              onBackToSummary={() => {
                setSummaryReturnSection("");
                scrollToSection(summarySectionRef);
              }}
            />
            {!scheduleComplete && (
              <p style={{ margin: "0 0 10px 0", color: "#92400e", fontSize: "12px" }}>
                Needs attention: select a slot or join the waitlist when no availability exists.
              </p>
            )}
            {/* F2: explained when a service change invalidated the chosen slot */}
            {slotInvalidatedNotice && (
              <div
                style={{
                  margin: "0 0 12px 0",
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #fcd34d",
                  background: "#fffbeb",
                  color: "#92400e",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                {slotInvalidatedNotice}
              </div>
            )}
            {waitlistJoined && !selectedTime && (
              <div
                style={{
                  margin: "0 0 12px 0",
                  padding: "10px 12px",
                  borderRadius: "6px",
                  border: "1px solid #86efac",
                  background: "#ecfdf5",
                  color: "#065f46",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                Waitlist request saved. Your waitlist is not active or guaranteed until you book an appointment.
              </div>
            )}
            {selectedTime && (
              <div
                style={{
                  margin: "0 0 12px 0",
                  padding: "12px 14px",
                  borderRadius: "10px",
                  border: "1px solid #fed7aa",
                  background: "#fff7ed",
                  color: "#9a3412",
                  fontSize: isMobile ? "11px" : "13px",
                  lineHeight: "1.5",
                }}
              >
                <strong>Availability check:</strong> {BOOKING_SLOT_HOLD_NOTICE}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: "24px",
              }}
            >
              {/* Calendar */}
              <div>
                <style>
                  {`
                    .booking-calendar-unavailable-date {
                      background-color: #f3f4f6 !important;
                      color: #9ca3af !important;
                      text-decoration: line-through;
                      cursor: not-allowed !important;
                    }

                    .booking-calendar-unavailable-date:enabled:hover,
                    .booking-calendar-unavailable-date:enabled:focus {
                      background-color: #f3f4f6 !important;
                    }

                    .booking-calendar-unavailable-dot {
                      display: inline-block;
                      margin-left: 4px;
                      width: 6px;
                      height: 6px;
                      border-radius: 999px;
                      background-color: #9ca3af;
                      vertical-align: middle;
                    }
                  `}
                </style>
                <Calendar
                  onChange={(value) => {
                    const pickedDate = Array.isArray(value) ? value[0] : value;
                    if (!pickedDate || isDateUnavailable(pickedDate)) {
                      return;
                    }
                    setSelectedDate(pickedDate);
                    setSelectedTime("");
                  }}
                  value={selectedDate}
                  minDate={new Date()}
                  tileDisabled={({ date }) => isDateUnavailable(date)}
                  tileClassName={({ date }) =>
                    isDateUnavailable(date)
                      ? "booking-calendar-unavailable-date"
                      : null
                  }
                  tileContent={({ date, view }) => {
                    if (view !== "month" || !isDateUnavailable(date)) {
                      return null;
                    }

                    return (
                      <span
                        className="booking-calendar-unavailable-dot"
                        title="Unavailable for booking (blocked or fully booked)."
                        aria-label="Unavailable date"
                      />
                    );
                  }}
                  style={{
                    width: "100%",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                  }}
                />
                <div
                  style={{
                    marginTop: "8px",
                    fontSize: isMobile ? "10px" : "11px",
                    color: "#6b7280",
                  }}
                >
                  Grayed-out dates are unavailable for booking.
                </div>
              </div>

              {/* Time Slots */}
              <div>
                {primarySelectionPending ? (
                  <div
                    style={{
                      padding: "16px",
                      borderRadius: "8px",
                      border: "1px dashed #f59e0b",
                      background: "#fffbeb",
                      color: "#92400e",
                      fontSize: isMobile ? "12px" : "13px",
                      fontWeight: "600",
                      lineHeight: 1.5,
                    }}
                  >
                    Pick a service option above to see available times — the
                    appointment length depends on the option you choose.
                  </div>
                ) : selectedDate ? (
                  <div>
                    {calendarLoading ? (
                      <div
                        style={{
                          fontSize: isMobile ? "11px" : "12px",
                          color: "#666",
                          textAlign: "center",
                          padding: "20px",
                        }}
                      >
                        Loading available times...
                      </div>
                    ) : availableSlots.length > 0 ? (
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(80px, 1fr))",
                          gap: "8px",
                        }}
                      >
                        {availableSlots.map((slot, index) => {
                          // Handle both string slots and object slots
                          const slotKey =
                            typeof slot === "string"
                              ? slot
                              : slot.time24 || slot.time12 || index;
                          const slotDisplay =
                            typeof slot === "string"
                              ? slot
                              : slot.time12 || slot.time24;
                          const slotValue =
                            typeof slot === "string"
                              ? slot
                              : slot.time24 || slot.time12;

                          return (
                            <button
                              key={`${slotKey}-${index}`}
                              type="button"
                              onClick={() => {
                                setSelectedTime(slotValue);
                                setSlotInvalidatedNotice("");
                                saveDirectBookingHold({
                                  shopId: resolveBookingLocationId(),
                                  staffId,
                                  serviceId,
                                  appointmentDate: selectedDate,
                                  startTime: slotValue,
                                });
                              }}
                              style={{
                                padding: "8px 12px",
                                border:
                                  selectedTime === slotValue
                                    ? "2px solid #2d6da3"
                                    : "1px solid #d1d5db",
                                borderRadius: "4px",
                                backgroundColor:
                                  selectedTime === slotValue
                                    ? "#f0f8ff"
                                    : "#fff",
                                fontSize: isMobile ? "10px" : "12px",
                                cursor: "pointer",
                                transition: "all 0.2s",
                              }}
                            >
                              {slotDisplay}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ display: "grid", gap: "12px" }}>
                        <div
                          style={{
                            fontSize: isMobile ? "11px" : "12px",
                            color: "#666",
                            textAlign: "center",
                            padding: "20px",
                          }}
                        >
                          No available times for this date
                        </div>

                        {nextAvailableSlots.length > 0 && (
                          <div
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: "8px",
                              padding: "12px",
                              background: "#fff",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: "700",
                                color: "#0f172a",
                                marginBottom: "8px",
                              }}
                            >
                              Next available dates and times
                            </div>
                            <div style={{ display: "grid", gap: "10px" }}>
                              {nextAvailableGroupedSlots.map((group) => (
                                <div
                                  key={group.dateKey}
                                  style={{
                                    border: "1px solid #e5e7eb",
                                    borderRadius: "8px",
                                    padding: "10px",
                                    background: "#fff",
                                  }}
                                >
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      fontWeight: "700",
                                      color: "#0f172a",
                                      marginBottom: "8px",
                                    }}
                                  >
                                    {group.displayDate}
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns:
                                        "repeat(auto-fit, minmax(120px, 1fr))",
                                      gap: "8px",
                                    }}
                                  >
                                    {group.slots.map((slot, idx) => (
                                      <button
                                        key={`${group.dateKey}-${slot.time24 || slot.time12 || idx}`}
                                        type="button"
                                        onClick={() => {
                                          setSelectedDate(new Date(`${slot.date}T12:00:00`));
                                          setSelectedTime(slot.time24 || slot.time12 || "");
                                          setSlotInvalidatedNotice("");
                                        }}
                                        style={{
                                          padding: "10px 10px",
                                          background: "#f9fafb",
                                          border: "2px solid #e5e7eb",
                                          borderRadius: "8px",
                                          cursor: "pointer",
                                          fontSize: isMobile ? "12px" : "13px",
                                          fontWeight: "600",
                                          color: "#374151",
                                          textAlign: "center",
                                          lineHeight: 1.25,
                                        }}
                                      >
                                        {slot.time12 || formatTime12(slot.time24)}
                                        {slot.endTime ? ` - ${formatTime12(slot.endTime)}` : ""}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {nextAvailableError ? (
                          <div
                            style={{
                              border: "1px solid #fecaca",
                              background: "#fef2f2",
                              color: "#991b1b",
                              borderRadius: "8px",
                              padding: "10px 12px",
                              fontSize: "12px",
                            }}
                          >
                            {nextAvailableError}
                          </div>
                        ) : nextAvailableSlots.length === 0 ? (
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              textAlign: "center",
                              padding: "4px 0 0",
                            }}
                          >
                            Looking for the next available openings...
                          </div>
                        ) : null}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: isMobile ? "11px" : "12px",
                      color: "#666",
                      textAlign: "center",
                      padding: "20px",
                    }}
                  >
                    Please select a date first
                  </div>
                )}
              </div>
            </div>

            {/* Waitlist Panel */}
          {staff &&
            ["R3", "R4", "R7"].includes(staff.role) &&
            selectedDate &&
            !calendarLoading &&
            availableSlots.length === 0 &&
            !selectedTime && (
            <div style={{ marginTop: "14px" }}>
              <div
                style={{
                  textAlign: "left",
                  padding: "16px",
                  background: "#f8fafc",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "flex-start" : "center",
                    flexDirection: isMobile ? "column" : "row",
                    gap: "10px",
                  }}
                >
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    <p
                      style={{
                        margin: 0,
                        color: "#0f172a",
                        fontSize: "14px",
                        fontWeight: "600",
                      }}
                    >
                      {isSelectedDateToday
                        ? sameDayWaitlistMessage
                        : "No slots are available for this date. Join the waitlist to receive alerts about open spots."}
                    </p>
                    {!isSelectedDateToday && (
                      <p
                        style={{
                          margin: 0,
                          color: "#b91c1c",
                          fontSize: "14px",
                          fontWeight: "600",
                        }}
                      >
                        Note: Joining waitlist does not activate or guarantee alerts until you complete a booking.
                      </p>
                    )}
                  </div>

                  {!isSelectedDateToday && (
                    <button
                      type="button"
                      disabled={
                        waitlistJoined ||
                        waitlistSubmitting ||
                        (!isAuthenticated && !waitlistContactReady)
                      }
                      onClick={handleJoinWaitlist}
                      style={{
                        padding: "10px 14px",
                        background: waitlistJoined ? "#16a34a" : "#111827",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor:
                          waitlistJoined || waitlistSubmitting
                            ? "not-allowed"
                            : "pointer",
                        fontWeight: "600",
                        fontSize: "14px",
                        opacity: waitlistJoined ? 0.9 : 1,
                      }}
                    >
                      {waitlistJoined
                        ? "Joined"
                        : waitlistSubmitting
                          ? "Joining..."
                          : "Join Waitlist"}
                    </button>
                  )}
                </div>

                {!isSelectedDateToday && !isAuthenticated && !waitlistContactReady && (
                  <p
                    style={{
                      marginTop: "8px",
                      marginBottom: 0,
                      color: "#92400e",
                      fontSize: "12px",
                    }}
                  >
                    Add your name and email in Your Information to join the waitlist.
                  </p>
                )}

                {waitlistMessage.text && (
                  <p
                    style={{
                      marginTop: "10px",
                      marginBottom: 0,
                      color:
                        waitlistMessage.type === "error" ? "#991b1b" : "#065f46",
                      fontSize: "13px",
                      fontWeight: "500",
                    }}
                  >
                    {waitlistMessage.text}
                  </p>
                )}

                {activeWaitlistConflict && (
                  <div
                    style={{
                      marginTop: "10px",
                      padding: "10px 12px",
                      border: "1px solid #fecaca",
                      borderRadius: "6px",
                      background: "#fff7ed",
                      color: "#7f1d1d",
                      fontSize: "13px",
                      lineHeight: 1.45,
                    }}
                  >
                    <div style={{ fontWeight: "700", marginBottom: "4px" }}>
                      Current waitlist request
                    </div>
                    <div>
                      <strong>Date:</strong>{" "}
                      {getWaitlistDateLabels(activeWaitlistConflict).join(", ") || "Date not available"}
                    </div>
                    <div>
                      <strong>Service(s):</strong>{" "}
                      {getWaitlistServiceLabels(activeWaitlistConflict).join(", ")}
                    </div>
                  </div>
                )}

                {pendingWaitlistReplacement && (
                  <div
                    style={{
                      marginTop: "10px",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    <button
                      type="button"
                      disabled={waitlistSubmitting}
                      onClick={() => handleJoinWaitlist({ replaceExistingWaitlist: true })}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "none",
                        background: "#991b1b",
                        color: "white",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: waitlistSubmitting ? "not-allowed" : "pointer",
                      }}
                    >
                      Replace Waitlist
                    </button>
                    <button
                      type="button"
                      disabled={waitlistSubmitting}
                      onClick={handleKeepExistingWaitlist}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        background: "#fff",
                        color: "#374151",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: waitlistSubmitting ? "not-allowed" : "pointer",
                      }}
                    >
                      Keep Existing
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          <div
            style={{
              marginTop: "14px",
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <button
              type="button"
              style={nextButtonStyle}
              onClick={() => goToPreviousSection("dateTime")}
            >
              Back
            </button>
            <button
              type="button"
              style={{
                ...nextButtonStyle,
                opacity: scheduleComplete ? 1 : 0.6,
                cursor: scheduleComplete ? "pointer" : "not-allowed",
              }}
              disabled={!scheduleComplete}
              onClick={() => advanceToNextSection("dateTime")}
            >
              Next
            </button>
          </div>
          </section>

          {/* Recommended Products */}
          {recommendedProducts && recommendedProducts.length > 0 && (
            <section
              ref={productsSectionRef}
              style={{
                marginBottom: "14px",
                padding: "16px",
                borderRadius: "8px",
                ...getSectionBlockStyle(
                  !isBillingAddressRequired(selectedProducts)
                    ? "optional"
                    : billingAddressComplete
                      ? "complete"
                      : "attention",
                ),
                ...getCollapsibleSectionStyle(expandedSection, "products"),
              }}
            >
              <SectionStatusHeader
                title="Recommended Products"
                state={
                  !isBillingAddressRequired(selectedProducts)
                    ? "optional"
                    : billingAddressComplete
                      ? "complete"
                      : "attention"
                }
                isMobile={isMobile}
                hideTopControls
                sectionExpanded={expandedSection === "products"}
              />
              {isBillingAddressRequired(selectedProducts) && !billingAddressComplete && (
                <p style={{ margin: "0 0 10px 0", color: "#92400e", fontSize: "12px" }}>
                  Needs attention: billing address is required when products are selected.
                </p>
              )}
              {isBillingAddressRequired(selectedProducts) && (
                <div
                  ref={billingAddressSectionRef}
                  style={{
                    marginBottom: "16px",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #bfdbfe",
                    background: "#eff6ff",
                  }}
                >
                  <h4
                    style={{
                      margin: "0 0 10px 0",
                      fontSize: "13px",
                      fontWeight: "700",
                      color: "#1d4ed8",
                    }}
                  >
                    Billing Address (Required for Product Tax)
                  </h4>
                  {shouldShowBillingAddressReturnPrompt(billingAddressReturnTarget) && (
                    <div
                      style={{
                        marginBottom: "12px",
                        padding: "10px 12px",
                        borderRadius: "6px",
                        background: "#dbeafe",
                        border: "1px solid #bfdbfe",
                        color: "#1d4ed8",
                        fontSize: "12px",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: "12px",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <span>Save your address here, then continue from the summary.</span>
                      <button
                        type="button"
                        onClick={() =>
                          summarySectionRef.current?.scrollIntoView({
                            behavior: "smooth",
                            block: "center",
                          })
                        }
                        style={{
                          padding: "8px 10px",
                          background: "#bfdbfe",
                          color: "#1d4ed8",
                          border: "1px solid #93c5fd",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        Back to Summary
                      </button>
                    </div>
                  )}

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: "16px",
                    }}
                  >
                    <AddressAutocompleteInput
                      ref={billingStreetInputRef}
                      value={customerAddress.street}
                      onChange={(val) =>
                        setCustomerAddress((prev) => ({ ...prev, street: val }))
                      }
                      onAddressSelect={({ street, city, state, zip, country }) =>
                        setCustomerAddress((prev) => ({
                          ...prev,
                          street: street || prev.street,
                          city: city || prev.city,
                          state: state || prev.state,
                          zipCode: zip || prev.zipCode,
                          country: country || prev.country,
                        }))
                      }
                      placeholder="Street Address"
                      required
                      inputStyle={{
                        padding: "12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: isMobile ? "12px" : "13px",
                        fontFamily: "inherit",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    />
                    <input
                      type="text"
                      placeholder="City"
                      value={customerAddress.city}
                      onChange={(e) =>
                        setCustomerAddress((prev) => ({
                          ...prev,
                          city: e.target.value,
                        }))
                      }
                      style={{
                        padding: "12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: isMobile ? "12px" : "13px",
                        fontFamily: "inherit",
                      }}
                      required
                    />
                    <input
                      type="text"
                      placeholder="State"
                      value={customerAddress.state}
                      onChange={(e) =>
                        setCustomerAddress((prev) => ({
                          ...prev,
                          state: e.target.value,
                        }))
                      }
                      style={{
                        padding: "12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: isMobile ? "12px" : "13px",
                        fontFamily: "inherit",
                      }}
                      required
                    />
                    <input
                      type="text"
                      placeholder="ZIP Code"
                      value={customerAddress.zipCode}
                      onChange={(e) =>
                        setCustomerAddress((prev) => ({
                          ...prev,
                          zipCode: e.target.value,
                        }))
                      }
                      style={{
                        padding: "12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: isMobile ? "12px" : "13px",
                        fontFamily: "inherit",
                      }}
                      required
                    />
                    <input
                      type="text"
                      placeholder="Country (e.g. US)"
                      value={customerAddress.country}
                      onChange={(e) =>
                        setCustomerAddress((prev) => ({
                          ...prev,
                          country: e.target.value,
                        }))
                      }
                      style={{
                        padding: "12px",
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        fontSize: isMobile ? "12px" : "13px",
                        fontFamily: "inherit",
                      }}
                      required
                    />
                  </div>
                </div>
              )}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    color: "#6b7280",
                    fontSize: "14px",
                  }}
                >
                  Great additions to your {service?.name}
                </p>
                <span
                  style={{
                    fontSize: "13px",
                    color: "#9ca3af",
                  }}
                >
                  Swipe to see more →
                </span>
              </div>

              {/* Horizontal Scrolling Container */}
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  overflowX: "auto",
                  overflowY: "hidden",
                  paddingBottom: "16px",
                  marginBottom: "16px",
                  scrollSnapType: "x mandatory",
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                  scrollbarColor: "#cbd5e1 #f1f5f9",
                }}
              >
                {recommendedProducts.slice(0, 10).map((product) => {
                  const hasSelectableVariants =
                    product?.hasVariants === true ||
                    Number(product?.variantCount) > 1 ||
                    (Array.isArray(product?.variants) &&
                      product.variants.length > 1);
                  const selected = selectedProducts.find(
                    (p) =>
                      p.productId === product._id ||
                      p.familyProductId === product._id,
                  );
                  const displayedProductPrice =
                    typeof selected?.price === "number"
                      ? selected.price
                      : typeof product.price === "number"
                        ? product.price
                        : Number(product.masterProductId?.basePrice) || 0;

                  return (
                    <div
                      key={product._id}
                      style={{
                        minWidth: "200px",
                        maxWidth: "200px",
                        background: selected ? "#f0fdf4" : "white",
                        border: selected
                          ? "2px solid #10b981"
                          : "2px solid #e5e7eb",
                        borderRadius: "12px",
                        padding: "12px",
                        scrollSnapAlign: "start",
                        display: "flex",
                        flexDirection: "column",
                        transition: "all 0.2s",
                        position: "relative",
                      }}
                    >
                      {(product.masterProductId?.images?.[0] ||
                        product.images?.[0]) && (
                        <div style={{ position: "relative" }}>
                          <img
                            src={
                              product.masterProductId?.images?.[0] ||
                              product.images?.[0]
                            }
                            alt={product.masterProductId?.name || product.name}
                            style={{
                              width: "100%",
                              height: "140px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              marginBottom: "12px",
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setQuickViewProduct(product);
                              setShowQuickViewModal(true);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setQuickViewProduct(product);
                              setShowQuickViewModal(true);
                            }}
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              background: "rgba(255, 255, 255, 0.95)",
                              border: "1px solid #e5e7eb",
                              borderRadius: "6px",
                              padding: "6px 10px",
                              cursor: "pointer",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#374151",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "white";
                              e.target.style.transform = "scale(1.05)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background =
                                "rgba(255, 255, 255, 0.95)";
                              e.target.style.transform = "scale(1)";
                            }}
                          >
                            👁️ Quick View
                          </button>
                        </div>
                      )}

                      <h4
                        style={{
                          margin: "0 0 8px 0",
                          fontSize: "14px",
                          fontWeight: "600",
                          color: "#374151",
                          lineHeight: "1.3",
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setQuickViewProduct(product);
                          setShowQuickViewModal(true);
                        }}
                      >
                        {product.masterProductId?.name || product.name}
                      </h4>

                      <p
                        style={{
                          margin: "0 0 12px 0",
                          fontSize: "14px",
                          fontWeight: "700",
                          color: "#2d6da3",
                        }}
                      >
                        ${displayedProductPrice.toFixed(2)}
                      </p>

                      {!selected ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (hasSelectableVariants) {
                              setQuickViewProduct(product);
                              setShowQuickViewModal(true);
                              return;
                            }
                            toggleProduct(product);
                          }}
                          style={{
                            padding: "8px 12px",
                            background: "#2d6da3",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: "600",
                            fontSize: "13px",
                            marginTop: "auto",
                          }}
                        >
                          {hasSelectableVariants ? "Choose Option" : "☐ Add"}
                        </button>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginTop: "auto",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() =>
                              updateProductQuantity(
                                selected.productId,
                                selected.quantity - 1,
                              )
                            }
                            style={{
                              width: "28px",
                              height: "28px",
                              background: "#f3f4f6",
                              border: "1px solid #d1d5db",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "16px",
                              fontWeight: "bold",
                            }}
                          >
                            −
                          </button>
                          <span
                            style={{
                              flex: 1,
                              textAlign: "center",
                              fontWeight: "600",
                              fontSize: "14px",
                            }}
                          >
                            {selected.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              updateProductQuantity(
                                selected.productId,
                                selected.quantity + 1,
                              )
                            }
                            style={{
                              width: "28px",
                              height: "28px",
                              background: "#f3f4f6",
                              border: "1px solid #d1d5db",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "16px",
                              fontWeight: "bold",
                            }}
                          >
                            +
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedProducts((previous) =>
                                previous.filter(
                                  (entry) =>
                                    entry.productId !== selected.productId,
                                ),
                              )
                            }
                            style={{
                              padding: "6px 10px",
                              background: "#fee2e2",
                              color: "#dc2626",
                              border: "none",
                              borderRadius: "4px",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: "600",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  background: "#eff6ff",
                  border: "1px solid #3b82f6",
                  borderRadius: "8px",
                  padding: "12px",
                  fontSize: "13px",
                  color: "#1e40af",
                }}
              >
                💡 Products available for pickup at your appointment
              </div>

              <div
                style={{
                  marginTop: "14px",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <button
                  type="button"
                  style={nextButtonStyle}
                  onClick={() => goToPreviousSection("products")}
                >
                  Back
                </button>
                <button
                  type="button"
                  style={{
                    ...nextButtonStyle,
                    opacity: billingAddressComplete ? 1 : 0.6,
                    cursor: billingAddressComplete ? "pointer" : "not-allowed",
                  }}
                  disabled={!billingAddressComplete}
                  onClick={() => advanceToNextSection("products")}
                >
                  Next
                </button>
              </div>
            </section>
          )}

          {/* Price Summary */}
          <section
            ref={summarySectionRef}
            style={{
              backgroundColor: "#f8f9fa",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "24px",
              border: "1px solid #e9ecef",
              ...getSectionBlockStyle(summaryReady ? "complete" : "attention"),
              ...getCollapsibleSectionStyle(expandedSection, "summary"),
            }}
          >
            <SectionStatusHeader
              title="Price Summary"
              state={summaryReady ? "complete" : "attention"}
              isMobile={isMobile}
              hideTopControls
              sectionExpanded={expandedSection === "summary"}
            />
            {!summaryReady && (
              <p style={{ margin: "0 0 10px 0", color: "#92400e", fontSize: "12px" }}>
                Needs attention: complete required sections before checkout.
              </p>
            )}
            {!summaryReady && (
              <BlockerChipsPanel
                blockers={summaryBlockers}
                onBlockerClick={handleSummaryBlockerClick}
              />
            )}
            <NotesPanel warnings={summarySoftWarnings} />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
                fontSize: isMobile ? "12px" : "13px",
              }}
            >
              <span>{primaryDisplayName} (Primary)</span>
              <span>
                {primarySelectionPending
                  ? "—"
                  : `$${(primaryRowPrice || 0).toFixed(2)}`}
              </span>
            </div>
            {bundleServices.slice(1).map((bundleSvc) => {
              // P4: the summary must show the add-on's RESOLVED line - its own
              // units / hours / option - not the catalog values. A 5-unit add-on
              // was itemised here at its single-unit price.
              const summaryAddOnSelection =
                addOnSelections[String(bundleSvc._id)] || {};
              const summaryAddOnLine = resolveServicePricing(
                bundleSvc,
                summaryAddOnSelection,
              );
              const summaryAddOnNeedsChoice = Boolean(
                summaryAddOnLine && summaryAddOnLine.requiresSelection,
              );

              return (
                <div
                  key={bundleSvc._id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                    fontSize: isMobile ? "11px" : "12px",
                    color: "#555",
                    paddingLeft: "8px",
                  }}
                >
                  <span>
                    {summaryAddOnLine?.label || bundleSvc.name} (Add-on)
                  </span>
                  <span>
                    {summaryAddOnNeedsChoice
                      ? "—"
                      : `$${(Number(summaryAddOnLine?.totalPrice) || 0).toFixed(2)}`}
                  </span>
                </div>
              );
            })}
            {selectedProducts.map((product) => (
              <div
                key={product.productId}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                  fontSize: isMobile ? "11px" : "12px",
                  color: "#666",
                  paddingLeft: "8px",
                }}
              >
                <span>
                  {product.name}
                  {getProductVariantSummary(product)
                    ? ` • ${getProductVariantSummary(product)}`
                    : ""}
                  {` x${product.quantity}`}
                </span>
                <span>${(product.price * product.quantity).toFixed(2)}</span>
              </div>
            ))}

            {selectedProducts.length > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                  fontSize: isMobile ? "11px" : "12px",
                  color: "#9ca3af",
                }}
              >
                <span>
                  {taxPreview.calculatedAtCheckout === false
                    ? `Tax (${taxPreview.taxRate.toFixed(2)}%)`
                    : "Tax"}
                </span>
                <span>
                  {taxLoading
                    ? "Calculating..."
                    : taxPreview.calculatedAtCheckout === false
                    ? `$${taxPreview.taxAmount.toFixed(2)}`
                    : "Calculated at checkout"}
                </span>
              </div>
            )}

            {serviceLocation === "client" && staff?.travelFee > 0 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "8px",
                  fontSize: isMobile ? "12px" : "13px",
                  color: "#2d6da3",
                }}
              >
                <span>Travel Fee</span>
                <span>${staff.travelFee}</span>
              </div>
            )}

            {/* Tip Selection */}
            <div style={{ marginBottom: "8px" }}>
              <div
                style={{
                  fontSize: isMobile ? "12px" : "13px",
                  fontWeight: "600",
                  marginBottom: "8px",
                  color: "#1a1a1a",
                }}
              >
                Add a Tip (on service only)
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
                  gap: isMobile ? "4px" : "6px",
                }}
              >
                {TIP_PRESET_PERCENTAGES.map((percent) => (
                  <label
                    key={percent}
                    style={{
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="radio"
                      name="tip"
                      value={percent}
                      checked={!tipIsCustom && tipPercentage === percent}
                      onChange={() => {
                        setTipIsCustom(false);
                        setTipPercentage(percent);
                      }}
                      style={{ display: "none" }}
                    />
                    <span
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        padding: isMobile ? "6px 2px" : "8px 4px",
                        border: "2px solid",
                        borderColor:
                          !tipIsCustom && tipPercentage === percent
                            ? "#4a90e2"
                            : "#e0e0e0",
                        borderRadius: "8px",
                        background:
                          !tipIsCustom && tipPercentage === percent
                            ? "#f0f7ff"
                            : "#ffffff",
                        transition: "all 0.2s",
                        fontSize: isMobile ? "11px" : "14px",
                        fontWeight: "600",
                        color:
                          !tipIsCustom && tipPercentage === percent
                            ? "#4a90e2"
                            : "#333333",
                        // Every card must stay on one line so the single row never breaks.
                        whiteSpace: "nowrap",
                      }}
                    >
                      {`${percent}%`}
                      <small
                        style={{
                          fontSize: isMobile ? "10px" : "12px",
                          fontWeight: "400",
                          color: "#666",
                          marginTop: "2px",
                        }}
                      >
                        {`$${(
                          ((effectiveServicePrice || service?.price || 0) *
                            percent) /
                          100
                        ).toFixed(2)}`}
                      </small>
                    </span>
                  </label>
                ))}
                <label
                  style={{
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="tip"
                    value="custom"
                    checked={tipIsCustom}
                    onChange={() => setTipIsCustom(true)}
                    style={{ display: "none" }}
                  />
                  <span
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      padding: isMobile ? "6px 2px" : "8px 4px",
                      border: "2px solid",
                      borderColor: tipIsCustom ? "#4a90e2" : "#e0e0e0",
                      borderRadius: "8px",
                      background: tipIsCustom ? "#f0f7ff" : "#ffffff",
                      transition: "all 0.2s",
                      fontSize: isMobile ? "11px" : "14px",
                      fontWeight: "600",
                      color: tipIsCustom ? "#4a90e2" : "#333333",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Custom
                    <small
                      style={{
                        fontSize: isMobile ? "10px" : "12px",
                        fontWeight: "400",
                        color: "#666",
                        marginTop: "2px",
                      }}
                    >
                      Your amount
                    </small>
                  </span>
                </label>
              </div>

              {tipIsCustom && (
                <div style={{ marginTop: "10px" }}>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    value={customTipAmount}
                    onChange={(e) => setCustomTipAmount(e.target.value)}
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      maxWidth: isMobile ? "100%" : "220px",
                      padding: "10px 12px",
                      fontSize: "16px",
                      border: customTipErrorFor(
                        effectiveServicePrice || service?.price || 0,
                      )
                        ? "2px solid #dc2626"
                        : "2px solid #e0e0e0",
                      borderRadius: "8px",
                    }}
                  />
                  {customTipErrorFor(
                    effectiveServicePrice || service?.price || 0,
                  ) ? (
                    <p
                      role="alert"
                      style={{
                        margin: "6px 0 0 0",
                        fontSize: isMobile ? "11px" : "12px",
                        fontWeight: "600",
                        color: "#dc2626",
                      }}
                    >
                      {customTipErrorFor(
                        effectiveServicePrice || service?.price || 0,
                      )}
                    </p>
                  ) : (
                    <p
                      style={{
                        margin: "6px 0 0 0",
                        fontSize: isMobile ? "11px" : "12px",
                        color: "#666",
                      }}
                    >
                      Enter amount.
                    </p>
                  )}
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "8px",
                  fontSize: isMobile ? "11px" : "12px",
                  color: "#666",
                }}
              >
                <span>
                  {tipIsCustom || tipPercentage === 0
                    ? "Tip Amount"
                    : `Tip Amount (${tipPercentage}%)`}
                </span>
                <span>
                  {tipIsCustom
                    ? `$${Math.max(0, parseCustomTip() || 0).toFixed(2)}`
                    : `$${(
                        ((effectiveServicePrice || service?.price || 0) *
                          tipPercentage) /
                        100
                      ).toFixed(2)}`}
                </span>
              </div>
            </div>

            <hr
              style={{
                margin: "8px 0",
                border: "none",
                borderTop: "1px solid #e9ecef",
              }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: isMobile ? "13px" : "14px",
                fontWeight: "700",
                color: "#1a1a1a",
              }}
            >
              <span>Total</span>
              <span>${calculateTotal()}</span>
            </div>

            <div
              style={{
                marginTop: "14px",
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <button
                type="button"
                style={nextButtonStyle}
                onClick={() => goToPreviousSection("summary")}
              >
                Back
              </button>
              <button
                type="button"
                style={{
                  ...nextButtonStyle,
                  visibility: "hidden",
                }}
                disabled
              >
                Next
              </button>
            </div>
          </section>

          {showFinalCheckoutStep && (
            <>
              {/* ── ONE LINE, ALL THE WORDING IN THE POLICY (2026-11-04) ─────────────────────────────
                  Same as the shop booking page: the summary just asks the customer to read and accept, and the
                  modal is the only place the terms are written. */}
              <div
                ref={policySectionRef}
                style={{
                  marginBottom: "16px",
                  padding: "14px 16px",
                  background: policyAccepted ? "#f2fbf5" : "#f7fafc",
                  border: `1px solid ${policyAccepted ? "#c6ebd4" : "#e2e8f0"}`,
                  borderRadius: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontSize: "13px",
                      color: "#4b5563",
                      lineHeight: 1.6,
                    }}
                  >
                    Please read and accept our{" "}
                    <button
                      type="button"
                      onClick={() => setShowPolicyModal(true)}
                      style={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#2563eb",
                        textDecoration: "underline",
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      Cancellation and Reschedule Policy
                    </button>
                    .
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      color: policyAccepted ? "#15803d" : "#6b7280",
                    }}
                  >
                    {policyAccepted ? "✓ Accepted — thank you" : "Needed before you pay"}
                  </span>
                </div>
              </div>

              <p
                style={{
                  fontSize: "12px",
                  color: "#6b7280",
                  lineHeight: "1.5",
                  margin: "-4px 0 16px 0",
                }}
              >
                {NON_REFUNDABLE_PROCESSING_FEE_SHORT_NOTICE}
              </p>

              {(waitlistCompatibilityLoading ||
                waitlistCompatibility?.hasPendingWaitlist) && (
                <div
                  ref={waitlistMismatchSectionRef}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "10px",
                    marginBottom: "16px",
                    padding: "12px 14px",
                    background:
                      waitlistCompatibility?.compatible === false
                        ? "#fff7ed"
                        : "#ecfdf5",
                    border: `1px solid ${
                      waitlistCompatibility?.compatible === false
                        ? "#fed7aa"
                        : "#86efac"
                    }`,
                    borderRadius: "10px",
                  }}
                >
                  {waitlistCompatibility?.compatible === false && (
                    <input
                      type="checkbox"
                      id="freelancer-waitlist-mismatch-accept"
                      checked={waitlistMismatchAcknowledged}
                      onChange={(e) =>
                        setWaitlistMismatchAcknowledged(e.target.checked)
                      }
                      style={{ marginTop: "2px", flexShrink: 0, cursor: "pointer" }}
                    />
                  )}
                  <label
                    htmlFor="freelancer-waitlist-mismatch-accept"
                    style={{
                      fontSize: "13px",
                      color:
                        waitlistCompatibility?.compatible === false
                          ? "#9a3412"
                          : "#065f46",
                      lineHeight: "1.5",
                      cursor:
                        waitlistCompatibility?.compatible === false
                          ? "pointer"
                          : "default",
                      userSelect: "none",
                    }}
                  >
                    {waitlistCompatibilityLoading
                      ? "Checking pending waitlist compatibility..."
                      : waitlistCompatibility?.compatible === false
                        ? "I understand this booking does not match my pending waitlist request. If I continue, that waitlist request will be cancelled."
                        : waitlistCompatibility?.message}
                  </label>
                </div>
              )}

              {/* Submit Button */}
              {waitlistOnlyPath ? (
                <>
                  <div
                    style={{
                      padding: "12px 14px",
                      borderRadius: "10px",
                      border: "1px solid #86efac",
                      background: "#ecfdf5",
                      color: "#065f46",
                      fontSize: "13px",
                      fontWeight: "600",
                      marginBottom: "10px",
                    }}
                  >
                    Waitlist request complete. No payment is required until a time becomes available.
                  </div>
                  <p
                    style={{
                      fontSize: "12px",
                      color: "#6b7280",
                      lineHeight: "1.5",
                      margin: "0 0 16px 0",
                    }}
                  >
                    We will notify you when a matching appointment opens.
                  </p>
                </>
              ) : (
                <>
                  <BlockerChipsPanel
                    title="Before submitting, resolve:"
                    blockers={submitBlockers}
                    onBlockerClick={handleSubmitBlockerClick}
                  />
                  <button
                    type="submit"
                    disabled={
                      paymentProcessing ||
                      primarySelectionPending ||
                      !selectedDate ||
                      !selectedTime ||
                      !policyAccepted ||
                      (serviceLocation === "client" &&
                        (!clientAddress.street ||
                          !clientAddress.city ||
                          !clientAddress.state ||
                          !clientAddress.zip ||
                          !travelFeeAccepted))
                    }
                    style={{
                      width: "100%",
                      padding: "16px",
                      backgroundColor: paymentProcessing || !policyAccepted ? "#ccc" : "#2d6da3",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: isMobile ? "14px" : "16px",
                      fontWeight: "600",
                      cursor: paymentProcessing || !policyAccepted ? "not-allowed" : "pointer",
                      transition: "background-color 0.2s",
                      opacity: paymentProcessing ? 0.7 : 1,
                    }}
                  >
                    {paymentProcessing
                      ? "Processing..."
                      : `Book & Pay $${calculateTotal()}`}
                  </button>
                </>
              )}
            </>
          )}
        </form>
      </div>

      <CancellationPolicyModal
        open={showPolicyModal}
        onClose={() => setShowPolicyModal(false)}
        policy={cancellationPolicy}
        acknowledgement={{
          accepted: policyAccepted,
          onAccept: setPolicyAccepted,
        }}
      />

      {showCartWarningModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "16px",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              maxWidth: "520px",
              width: "100%",
              padding: "20px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.2)",
            }}
          >
            <h3
              style={{
                margin: "0 0 8px 0",
                fontSize: "16px",
                fontWeight: "600",
                color: "#111827",
              }}
            >
              You Have Cart Items
            </h3>
            <p
              style={{
                margin: "0 0 16px 0",
                color: "#4b5563",
                fontSize: "13px",
                lineHeight: 1.45,
              }}
            >
              You currently have {itemCount} item{itemCount > 1 ? "s" : ""}{" "}
              in your cart. Cart items are not included in this booking payment.
            </p>
            <div
              style={{
                display: "flex",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setCartWarningAcknowledged(true);
                  setShowCartWarningModal(false);
                }}
                style={{
                  padding: "10px 14px",
                  background: "#111827",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Continue Booking Only
              </button>
              <button
                type="button"
                onClick={() => navigate("/cart")}
                style={{
                  padding: "10px 14px",
                  background: "#e5e7eb",
                  color: "#111827",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Review Cart
              </button>
              <button
                type="button"
                onClick={() => navigate("/checkout")}
                style={{
                  padding: "10px 14px",
                  background: "#dbeafe",
                  color: "#1d4ed8",
                  border: "none",
                  borderRadius: "8px",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Go To Cart Checkout
              </button>
              <button
                type="button"
                onClick={() => setShowCartWarningModal(false)}
                style={{
                  padding: "10px 14px",
                  background: "transparent",
                  color: "#6b7280",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  fontSize: "13px",
                  cursor: "pointer",
                  marginLeft: "auto",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Authentication Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authModalMode}
        showGuestOption={true}
        onGuestCheckout={() => {
          setShowAuthModal(false);
          setIsAuthenticated(false);
          setHasChosenGuest(true);
          // Guest - customerInfo stays empty for manual entry
        }}
        onSuccess={async (data) => {
          setIsAuthenticated(true);
          setShowAuthModal(false);

          if (data.user) {
            setCustomerInfo({
              firstName:
                data.user.firstName || data.user.name?.split(" ")[0] || "",
              lastName:
                data.user.lastName || data.user.name?.split(" ")[1] || "",
              email: data.user.email || "",
              phone: data.user.phone || "",
            });
          }
        }}
      />

      {/* Product Quick View Modal */}
      {showQuickViewModal && (
        <ProductQuickViewModal
          product={quickViewProduct}
          onClose={() => {
            setShowQuickViewModal(false);
            setQuickViewProduct(null);
          }}
          onAddToBooking={(product, selectedVariant) => {
            toggleProduct(product, selectedVariant);
          }}
        />
      )}

      <CustomerFooter />
    </>
  );
};

export default FreelancerBooking;
